/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { EX, TABATA_EX_BASE } from './exercises.js';
import { getMascotMessage } from './mascotMessages.js';
import { LOGO_LIGHT_DATA, MASCOT_BUST_DATA, NAV_ICON_MUSCU, NAV_ICON_TABATA, NAV_ICON_STATS, NAV_ICON_PROFILE } from './assets.js';
import { generateAdaptedPrograms } from './programGenerator.js';
import { getLevelFromXP } from './xpSystem.js';

// ─── CONSTANTES ───────────────────────────────────────────────────────────────
const ALL_MUSCLES = [
  "Pectoraux","Dos","Épaules","Biceps","Triceps",
  "Quadriceps","Ischio-jambiers","Fessiers","Mollets",
  "Abdominaux","Trapèzes","Avant-bras","Cardio / Full body",
];

// ─── TONNAGE CHART ────────────────────────────────────────────────────────────
function TonnageChart({sessions, progId, C}) {
  const [activeIdx, setActiveIdx] = useState(null);

  const relevant = sessions
    .filter(s => s.programId === progId && s.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-10);

  const SLOTS = 10;
  const W = 280; const CHART_H = 60; const PAD_X = 12; const PAD_Y = 8;
  const DATE_ROW = 14; // hauteur réservée aux dates au-dessus
  const TOTAL_H = DATE_ROW + CHART_H;

  const data = relevant.map(s => ({
    label: new Date(s.date).toLocaleDateString("fr-FR", {day:"2-digit", month:"2-digit"}),
    tonnage: (s.exercises||[]).reduce((acc, ex) =>
      acc + (ex.sets||[]).reduce((a, set) =>
        a + (parseFloat(set.weight)||0) * (parseInt(set.reps)||0), 0), 0)
  }));

  const filled = data.length;
  const max = Math.max(...data.map(d => d.tonnage), 1);
  const delta = filled >= 2 && data[filled-2].tonnage > 0
    ? Math.round((data[filled-1].tonnage - data[filled-2].tonnage) / data[filled-2].tonnage * 100)
    : null;

  // X sur 10 slots fixes, données alignées à droite
  const getX = (slotIdx) => PAD_X + (slotIdx / (SLOTS - 1)) * (W - PAD_X * 2);
  // Y dans la zone chart (en dessous du DATE_ROW)
  const getY = (tonnage) => DATE_ROW + CHART_H - PAD_Y - ((tonnage / max) * (CHART_H - PAD_Y * 2));

  const points = data.map((d, i) => ({
    ...d,
    slotIdx: SLOTS - filled + i,
    x: getX(SLOTS - filled + i),
    y: getY(d.tonnage),
  }));

  const polyline = points.map(p => `${p.x},${p.y}`).join(" ");
  const activePoint = activeIdx !== null ? points[activeIdx] : null;

  return (
    <div style={{margin:"8px 14px 0",padding:"10px 12px",background:`${C.accent}08`,borderRadius:11,border:`1px solid ${C.accent}20`}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
        <p style={{color:C.muted,fontSize:10,fontWeight:700,margin:0,textTransform:"uppercase",letterSpacing:1}}>
          Tonnage · 10 dernières séances
        </p>

      </div>

      {filled === 0 ? (
        <div style={{height:TOTAL_H+8,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <p style={{color:C.muted,fontSize:11,fontWeight:600,margin:0,textAlign:"center",opacity:0.6}}>
            Enregistre ta première séance
          </p>
        </div>
      ) : (
        <>
          <svg width="100%" viewBox={`0 0 ${W} ${TOTAL_H}`} style={{display:"block",overflow:"visible"}}>
            {/* Dates au-dessus des points, centrées sur x */}
            {points.map((p, i) => {
              const isLast = i === filled-1;
              const isActive = activeIdx === i;
              return (
                <text key={`d${i}`}
                  x={p.x} y={DATE_ROW - 2}
                  textAnchor="middle"
                  fill={isActive ? C.accent : isLast ? C.accent : C.muted}
                  fontSize="8"
                  fontWeight={isLast || isActive ? "700" : "400"}
                  opacity={isActive ? 1 : isLast ? 0.9 : 0.5}
                >
                  {p.label}
                </text>
              );
            })}

            {/* Ligne de base */}
            <line x1={PAD_X} y1={DATE_ROW + CHART_H - PAD_Y} x2={W-PAD_X} y2={DATE_ROW + CHART_H - PAD_Y}
              stroke={`${C.accent}20`} strokeWidth="1"/>

            {/* Grille verticale fantôme sur les 10 slots */}
            {Array.from({length:SLOTS},(_,i)=>(
              <line key={i}
                x1={getX(i)} y1={DATE_ROW + PAD_Y}
                x2={getX(i)} y2={DATE_ROW + CHART_H - PAD_Y}
                stroke={`${C.border}25`} strokeWidth="1" strokeDasharray="2,3"/>
            ))}

            {/* Aire + courbe */}
            {filled >= 2 && <>
              <polygon
                points={`${points[0].x},${DATE_ROW+CHART_H-PAD_Y} ${polyline} ${points[filled-1].x},${DATE_ROW+CHART_H-PAD_Y}`}
                fill={`${C.accent}12`}/>
              <polyline points={polyline} fill="none" stroke={C.accent}
                strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.8"/>
            </>}

            {/* Points cliquables */}
            {points.map((p, i) => {
              const isLast = i === filled-1;
              const isActive = activeIdx === i;
              return (
                <g key={i} style={{cursor:"pointer"}} onClick={() => setActiveIdx(activeIdx === i ? null : i)}>
                  <circle cx={p.x} cy={p.y} r="12" fill="transparent"/>
                  {isActive && <circle cx={p.x} cy={p.y} r="8" fill={`${C.accent}30`}/>}
                  {isLast && !isActive && <circle cx={p.x} cy={p.y} r="6" fill={`${C.accent}25`}/>}
                  <circle cx={p.x} cy={p.y} r={isActive ? 4 : isLast ? 3.5 : 2.5}
                    fill={isActive || isLast ? C.accent : `${C.accent}80`}
                    stroke={C.bg} strokeWidth="1.5"/>
                </g>
              );
            })}
          </svg>

          {delta !== null && (
            <p style={{color:delta>=0?C.accent:"#ef4444",fontSize:11,fontWeight:800,margin:"4px 0 0",textAlign:"center"}}>
              {delta>=0?`+${delta}% vs séance précédente`:`${delta}% vs séance précédente`}
            </p>
          )}
          <p style={{color:activePoint?C.accent:C.muted,fontSize:activePoint?10:10,fontWeight:activePoint?800:600,margin:"4px 0 0",textAlign:"center",opacity:activePoint?1:0.6}}>
            {activePoint ? `${Math.round(activePoint.tonnage).toLocaleString("fr-FR")} kg` : (filled===1?"Reviens après ta prochaine séance pour voir ta progression":"")}
          </p>
        </>
      )}
    </div>
  );
}

const fmtRest = (s) => {
  if (!s) return "1min30";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec}s`;
  if (sec === 0) return `${m}min`;
  return `${m}min${sec}`;
};

// ─── SHARED HELPERS (importés depuis App.js via props ou re-déclarés ici) ────
// mkBtn, ConfirmModal, MM sont passés via le prop C ou importés
// Pour éviter la duplication, on les importe depuis un fichier partagé.
// En attendant la création de utils.js, ils sont reçus en props (mkBtn via C pattern).

// ─── UTILS locaux réutilisés dans ce fichier ─────────────────────────────────
const mkBtn = C => ({children,onClick,variant="primary",small,full,disabled,style:s={}}) => {
  const v = {
    primary:{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,color:"white",boxShadow:`0 4px 20px ${C.glow}`},
    ghost:  {background:C.faint,color:C.muted,border:`1px solid ${C.border}`},
    outline:{background:"transparent",color:C.accent,border:`1px solid ${C.accent}`},
    danger: {background:"rgba(239,68,68,0.1)",color:C.red,border:"1px solid rgba(239,68,68,0.25)"},
  };
  return <button onClick={disabled?undefined:onClick} style={{border:"none",fontWeight:800,borderRadius:12,cursor:disabled?"default":"pointer",opacity:disabled?0.35:1,fontSize:small?13:15,letterSpacing:0.4,padding:small?"8px 16px":"14px 20px",width:full?"100%":undefined,transition:"all 0.15s",...v[variant],...s}}>{children}</button>;
};

const ConfirmModal = ({message,onConfirm,onCancel,confirmLabel,cancelLabel,C}) => (
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:"0 24px"}}>
    <div style={{background:C.card,borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:380,border:`1px solid ${C.border}`}}>
      <p style={{color:C.text,fontWeight:800,fontSize:17,margin:"0 0 8px",textAlign:"center"}}>Confirmer</p>
      <p style={{color:C.muted,fontSize:14,margin:"0 0 24px",textAlign:"center",lineHeight:1.5}}>{message}</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <button onClick={onCancel} style={{padding:"12px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.muted,fontWeight:800,cursor:"pointer"}}>{cancelLabel||"Annuler"}</button>
        <button onClick={onConfirm} style={{padding:"12px",background:confirmLabel?"rgba(59,130,246,0.12)":"rgba(239,68,68,0.12)",border:confirmLabel?"1px solid rgba(59,130,246,0.3)":"1px solid rgba(239,68,68,0.3)",borderRadius:12,color:confirmLabel?C.accent:C.red,fontWeight:800,cursor:"pointer"}}>{confirmLabel||"Supprimer"}</button>
      </div>
    </div>
  </div>
);

const MM = {
  "Pectoraux":{color:"#ef4444",bg:"rgba(239,68,68,0.12)"},
  "Dos":{color:"#8b5cf6",bg:"rgba(139,92,246,0.12)"},
  "Épaules":{color:"#eab308",bg:"rgba(234,179,8,0.12)"},
  "Biceps":             {color:"#f97316",bg:"rgba(249,115,22,0.12)"},
  "Triceps":            {color:"#06b6d4",bg:"rgba(6,182,212,0.12)"},
  "Quadriceps":         {color:"#3b82f6",bg:"rgba(59,130,246,0.12)"},
  "Ischio-jambiers":    {color:"#10b981",bg:"rgba(16,185,129,0.12)"},
  "Fessiers":           {color:"#ec4899",bg:"rgba(236,72,153,0.12)"},
  "Mollets":            {color:"#14b8a6",bg:"rgba(20,184,166,0.12)"},
  "Abdominaux":         {color:"#f59e0b",bg:"rgba(245,158,11,0.12)"},
  "Trapèzes":           {color:"#a78bfa",bg:"rgba(167,139,250,0.12)"},
  "Avant-bras":         {color:"#fb923c",bg:"rgba(251,146,60,0.12)"},
  "Cardio / Full body": {color:"#84cc16",bg:"rgba(132,204,22,0.12)"},
};

const Tag = ({label,color,bg}) => (
  <span style={{background:bg,color,fontSize:11,padding:"3px 10px",borderRadius:20,fontWeight:700}}>{label}</span>
);

function AddCustomExModal({onAdd,onClose,isTabata,C}) {
  const [name,setName]=useState("");
  const [muscle,setMuscle]=useState("Pectoraux");
  const handleAdd=()=>{
    if(!name.trim())return;
    onAdd({id:`custom-${Date.now()}`,name:name.trim(),muscle,isCustom:true});
  };
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:"0 24px"}}>
      <div style={{background:C.card,borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:380,border:`1px solid ${C.border}`}}>
        <p style={{color:C.text,fontWeight:800,fontSize:17,margin:"0 0 16px"}}>Exercice personnalisé</p>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="Nom de l'exercice"
          style={{width:"100%",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:15,padding:"11px 13px",outline:"none",boxSizing:"border-box",marginBottom:12}}/>
        <select value={muscle} onChange={e=>setMuscle(e.target.value)}
          style={{width:"100%",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:15,padding:"11px 13px",outline:"none",marginBottom:20}}>
          {(isTabata?[...new Set(TABATA_EX_BASE.map(ex=>ex.muscle))]:ALL_MUSCLES).map(m=><option key={m} value={m}>{m}</option>)}
        </select>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <button onClick={onClose} style={{padding:"12px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.muted,fontWeight:800,cursor:"pointer"}}>Annuler</button>
          <button onClick={handleAdd} disabled={!name.trim()} style={{padding:"12px",background:name.trim()?`linear-gradient(135deg,${C.accent},${C.accent2})`:C.faint,border:"none",borderRadius:12,color:name.trim()?"white":C.muted,fontWeight:800,cursor:name.trim()?"pointer":"default"}}>Ajouter</button>
        </div>
      </div>
    </div>
  );
}

function ExPicker({onPick,onClose,C,isTabata}) {
  const [filter,setFilter]=useState("Tous");
  const [search,setSearch]=useState("");
  const [showCustom,setShowCustom]=useState(false);
  const DB=isTabata?TABATA_EX_BASE:EX;
  const filtered=DB.filter(ex=>(filter==="Tous"||ex.muscle===filter)&&ex.name.toLowerCase().includes(search.toLowerCase()));
  return(
    <div style={{position:"fixed",inset:0,background:C.bg,zIndex:500,display:"flex",flexDirection:"column"}}>
      {showCustom&&<AddCustomExModal onAdd={ex=>{onPick(ex);setShowCustom(false);}} onClose={()=>setShowCustom(false)} isTabata={isTabata} C={C}/>}
      <div style={{padding:"calc(env(safe-area-inset-top) + 20px) 20px 12px",background:C.surface,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
          <h2 style={{color:C.text,fontSize:17,fontWeight:900,margin:0}}>Choisir un exercice</h2>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher…"
          style={{width:"100%",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:15,padding:"10px 13px",outline:"none",boxSizing:"border-box"}}/>
      </div>
      <div style={{display:"flex",gap:8,padding:"10px 20px",overflowX:"auto",flexShrink:0}}>
        {["Tous",...(isTabata?[...new Set(DB.map(ex=>ex.muscle))]:ALL_MUSCLES)].map(m=><button key={m} onClick={()=>setFilter(m)} style={{flexShrink:0,padding:"6px 13px",borderRadius:20,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,background:filter===m?C.accent:C.faint,color:filter===m?"white":C.muted}}>{m}</button>)}
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"0 20px 20px",display:"flex",flexDirection:"column",gap:7}}>
        <div onClick={()=>setShowCustom(true)} style={{background:`${C.accent}10`,border:`1px dashed ${C.accent}`,borderRadius:12,padding:"11px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
          <span style={{color:C.accent,fontWeight:800,fontSize:13}}>+ Exercice personnalisé</span>
        </div>
        {filtered.map(ex=>{const meta=MM[ex.muscle]||{};return(
          <div key={ex.id} onClick={()=>onPick(ex)} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"10px 13px",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <p style={{color:C.text,fontWeight:700,margin:"0 0 3px",fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ex.name}</p>
              <Tag label={ex.muscle} color={meta.color||C.muted} bg={meta.bg||C.faint}/>
            </div>
            <span style={{color:C.accent,fontSize:18,fontWeight:900}}>+</span>
          </div>
        );})}
      </div>
    </div>
  );
}

function ParamsModal({profile,onSave,onClose,C}) {
  const [form,setForm]=useState(profile||{duration:60,days:null,equipment:"salle",homeEquip:[]});
  const set=(k,v)=>setForm(p=>({...p,[k]:v}));
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",backdropFilter:"blur(10px)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:300}} onClick={onClose}>
      <div style={{background:C.card,borderRadius:"24px 24px 0 0",padding:"28px 24px 16px",width:"100%",maxWidth:480,border:`1px solid ${C.border}`,maxHeight:"calc(100vh - env(safe-area-inset-bottom) - 75px)",overflowY:"auto",paddingBottom:"calc(env(safe-area-inset-bottom) + 80px)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <p style={{color:C.text,fontWeight:900,fontSize:16,margin:0}}>Adapter mon programme</p>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:28,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>DURÉE PAR SÉANCE</label>
        <div style={{display:"flex",gap:6,marginBottom:16}}>
          {[{v:30,label:"30m"},{v:45,label:"45m"},{v:60,label:"1h"},{v:75,label:"1h15"},{v:90,label:"1h30"}].map(d=>(
            <button key={d.v} onClick={()=>set("duration",d.v)}
              style={{flex:1,padding:"11px 2px",borderRadius:12,border:`1px solid ${form.duration===d.v?C.accent:C.border}`,background:form.duration===d.v?`${C.accent}15`:C.faint,cursor:"pointer",transition:"all 0.15s",textAlign:"center"}}>
              <span style={{color:form.duration===d.v?C.accent:C.text,fontWeight:800,fontSize:12,display:"block"}}>{d.label}</span>
              {form.duration===d.v&&<div style={{width:5,height:5,borderRadius:"50%",background:C.accent,margin:"3px auto 0"}}/>}
            </button>
          ))}
        </div>
        <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>JOURS PAR SEMAINE</label>
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          {[{v:3,label:"3 jours"},{v:4,label:"4 jours"},{v:5,label:"5 jours"}].map(d=>(
            <button key={d.v} onClick={()=>set("days",d.v)}
              style={{flex:1,padding:"11px 6px",borderRadius:11,border:`1px solid ${form.days===d.v?C.accent:C.border}`,background:form.days===d.v?`${C.accent}15`:C.faint,cursor:"pointer",transition:"all 0.15s",textAlign:"center"}}>
              <p style={{color:form.days===d.v?C.accent:C.text,fontWeight:800,fontSize:15,margin:"0 0 1px"}}>{d.v}</p>
              <p style={{color:C.muted,fontSize:10,margin:0}}>{d.v===3?"Flexible":d.v===4?"Régulier":"Intensif"}</p>
            </button>
          ))}
        </div>
        <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>ÉQUIPEMENT</label>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          <button onClick={()=>set("equipment","salle")}
            style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderRadius:12,border:`2px solid ${form.equipment==="salle"?C.accent:C.border}`,background:form.equipment==="salle"?`${C.accent}10`:C.faint,cursor:"pointer",transition:"all 0.15s"}}>
            <div style={{flex:1,textAlign:"left"}}>
              <p style={{color:form.equipment==="salle"?C.accent:C.text,fontWeight:800,fontSize:13,margin:"0 0 1px"}}>Salle de sport</p>
              <p style={{color:C.muted,fontSize:11,margin:0}}>Accès complet aux machines</p>
            </div>
            {form.equipment==="salle"&&<div style={{width:20,height:20,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{color:"white",fontSize:11,fontWeight:900}}>✓</span></div>}
          </button>
          <button onClick={()=>set("equipment","maison")}
            style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderRadius:12,border:`2px solid ${form.equipment==="maison"?C.accent:C.border}`,background:form.equipment==="maison"?`${C.accent}10`:C.faint,cursor:"pointer",transition:"all 0.15s"}}>
            <div style={{flex:1,textAlign:"left"}}>
              <p style={{color:form.equipment==="maison"?C.accent:C.text,fontWeight:800,fontSize:13,margin:"0 0 1px"}}>À la maison</p>
              <p style={{color:C.muted,fontSize:11,margin:0}}>Exercices adaptés</p>
            </div>
            {form.equipment==="maison"&&<div style={{width:20,height:20,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{color:"white",fontSize:11,fontWeight:900}}>✓</span></div>}
          </button>
        </div>
        {form.equipment==="maison"&&(
          <div style={{marginBottom:16}}>
            <p style={{color:C.text,fontSize:12,fontWeight:700,margin:"0 0 10px"}}>Quel équipement as-tu ?</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:8}}>
              {[{id:"halteres",label:"Haltères"},{id:"barre",label:"Barre"},{id:"kettlebells",label:"Kettlebells"},{id:"banc",label:"Banc"},{id:"barre_traction",label:"Traction"},{id:"elastiques",label:"Élastiques"}].map(eq=>{
                const has=(form.homeEquip||[]).includes(eq.id);
                return(
                  <button key={eq.id} onClick={()=>{const cur=form.homeEquip||[];set("homeEquip",has?cur.filter(e=>e!==eq.id):[...cur,eq.id]);}}
                    style={{padding:"10px 8px",borderRadius:10,border:`1px solid ${has?C.accent:C.border}`,background:has?`${C.accent}15`:C.faint,cursor:"pointer",textAlign:"center"}}>
                    <span style={{color:has?C.accent:C.text,fontSize:12,fontWeight:700}}>{eq.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <button onClick={()=>{onSave(form);onClose();}}
          style={{width:"100%",padding:"14px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:14,color:"white",fontWeight:800,fontSize:15,cursor:"pointer"}}>
          Enregistrer et régénérer
        </button>
      </div>
    </div>
  );
}

// ─── GENERATE PROGRAM PAGE ────────────────────────────────────────────────────
function GenerateProgramPage({profile, sessions, prs, exerciseWeights, onGenerated, onBack, C}) {
  const [form, setForm] = useState(() => {
    let savedDuration = 60, savedDays = 3;
    let savedEquipment = profile?.equipment || "salle";
    let savedHomeEquip = profile?.homeEquip || [];
    try {
      const s = localStorage.getItem('calestan_gen_prefs');
      if (s) { const p = JSON.parse(s); savedDuration = p.duration || 60; savedDays = p.days || 3; if (p.equipment) savedEquipment = p.equipment; if (p.homeEquip) savedHomeEquip = p.homeEquip; }
    } catch {}
    return {
      duration: savedDuration,
      days: savedDays,
      gender: profile?.gender || "homme",
      equipment: savedEquipment,
      homeEquip: savedHomeEquip,
    };
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => {
    setForm(p => {
      const next = {...p, [k]: v};
      try { localStorage.setItem('calestan_gen_prefs', JSON.stringify({duration: next.duration, days: next.days, equipment: next.equipment, homeEquip: next.homeEquip})); } catch {}
      return next;
    });
  };

  const handleGenerate = () => {
    setError(null);
    setLoading(true);
    try {
      const generated = generateAdaptedPrograms(
        form.duration, form.days, form.gender,
        form.equipment, form.homeEquip,
        exerciseWeights
      );
      if (!generated || generated.length === 0) throw new Error("Aucun programme généré.");
      onGenerated(generated, false);
    } catch(e) {
      setError(e.message || "Erreur lors de la génération.");
      setLoading(false);
    }
  };

  const EQUIP_LIST = [
    {id:"halteres",label:"Haltères"},{id:"barre",label:"Barre"},
    {id:"kettlebells",label:"Kettlebells"},{id:"banc",label:"Banc"},
    {id:"barre_traction",label:"Traction"},{id:"elastiques",label:"Élastiques"},
  ];

  return (
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,display:"flex",flexDirection:"column",zIndex:200}}>
      {/* Header fixe */}
      <div style={{flexShrink:0,padding:"calc(env(safe-area-inset-top) + 20px) 20px 16px",display:"flex",alignItems:"center",gap:12,background:C.bg,borderBottom:`1px solid ${C.border}`}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:"0 4px 0 0",flexShrink:0}}>←</button>
        <div>
          <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 2px"}}>MON PROGRAMME</p>
          <h1 style={{color:C.text,fontSize:24,fontWeight:900,margin:0}}>Générer</h1>
        </div>
      </div>

      {/* Contenu scrollable */}
      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"20px 20px 24px"}}>
        {/* Durée */}
        <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>DURÉE PAR SÉANCE</p>
        <div style={{display:"flex",gap:6,marginBottom:20}}>
          {[{v:30,label:"30m"},{v:45,label:"45m"},{v:60,label:"1h"},{v:75,label:"1h15"},{v:90,label:"1h30"}].map(d=>(
            <button key={d.v} onClick={()=>set("duration",d.v)}
              style={{flex:1,padding:"11px 2px",borderRadius:12,border:`1px solid ${form.duration===d.v?C.accent:C.border}`,
                background:form.duration===d.v?`${C.accent}15`:C.faint,cursor:"pointer",textAlign:"center"}}>
              <span style={{color:form.duration===d.v?C.accent:C.text,fontWeight:800,fontSize:12}}>{d.label}</span>
            </button>
          ))}
        </div>

        {/* Jours */}
        <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>JOURS PAR SEMAINE</p>
        <div style={{display:"flex",gap:8,marginBottom:20}}>
          {[{v:3,sub:"Flexible"},{v:4,sub:"Régulier"},{v:5,sub:"Intensif"}].map(d=>(
            <button key={d.v} onClick={()=>set("days",d.v)}
              style={{flex:1,padding:"11px 6px",borderRadius:11,border:`1px solid ${form.days===d.v?C.accent:C.border}`,
                background:form.days===d.v?`${C.accent}15`:C.faint,cursor:"pointer",textAlign:"center"}}>
              <p style={{color:form.days===d.v?C.accent:C.text,fontWeight:800,fontSize:15,margin:"0 0 1px"}}>{d.v}</p>
              <p style={{color:C.muted,fontSize:10,margin:0}}>{d.sub}</p>
            </button>
          ))}
        </div>

        {/* Genre — masqué si déjà défini dans le profil */}
        {!profile?.gender&&(
          <>
            <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>GENRE</p>
            <div style={{display:"flex",gap:8,marginBottom:20}}>
              {[{v:"homme",label:"Homme"},{v:"femme",label:"Femme"}].map(g=>(
                <button key={g.v} onClick={()=>set("gender",g.v)}
                  style={{flex:1,padding:"11px",borderRadius:11,border:`1px solid ${form.gender===g.v?C.accent:C.border}`,
                    background:form.gender===g.v?`${C.accent}15`:C.faint,cursor:"pointer",
                    color:form.gender===g.v?C.accent:C.text,fontWeight:700,fontSize:14}}>
                  {g.label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Équipement */}
        <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>ÉQUIPEMENT</p>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:form.equipment==="maison"?12:20}}>
          {[{v:"salle",label:"Salle de sport",sub:"Accès complet aux machines"},{v:"maison",label:"À la maison",sub:"Exercices adaptés"}].map(e=>(
            <button key={e.v} onClick={()=>set("equipment",e.v)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderRadius:12,
                border:`2px solid ${form.equipment===e.v?C.accent:C.border}`,
                background:form.equipment===e.v?`${C.accent}10`:C.faint,cursor:"pointer",textAlign:"left"}}>
              <div style={{flex:1}}>
                <p style={{color:form.equipment===e.v?C.accent:C.text,fontWeight:800,fontSize:13,margin:"0 0 1px"}}>{e.label}</p>
                <p style={{color:C.muted,fontSize:11,margin:0}}>{e.sub}</p>
              </div>
              {form.equipment===e.v&&<div style={{width:20,height:20,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><span style={{color:"white",fontSize:11,fontWeight:900}}>✓</span></div>}
            </button>
          ))}
        </div>
        {form.equipment==="maison"&&(
          <div style={{marginBottom:20}}>
            <p style={{color:C.text,fontSize:12,fontWeight:700,margin:"0 0 10px"}}>Équipement disponible</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:8}}>
              {EQUIP_LIST.map(eq=>{
                const has=(form.homeEquip||[]).includes(eq.id);
                return(
                  <button key={eq.id} onClick={()=>{const cur=form.homeEquip||[];set("homeEquip",has?cur.filter(e=>e!==eq.id):[...cur,eq.id]);}}
                    style={{padding:"10px 8px",borderRadius:10,border:`1px solid ${has?C.accent:C.border}`,
                      background:has?`${C.accent}15`:C.faint,cursor:"pointer",textAlign:"center"}}>
                    <span style={{color:has?C.accent:C.text,fontSize:12,fontWeight:700}}>{eq.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Erreur */}
        {error&&(
          <div style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:12,padding:"12px 14px",marginBottom:16}}>
            <p style={{color:"#ef4444",fontSize:13,margin:0}}>{error}</p>
          </div>
        )}
      </div>

      {/* Bouton fixe en bas */}
      <div style={{flexShrink:0,padding:`16px 20px calc(env(safe-area-inset-bottom) + 16px)`,background:C.bg,borderTop:`1px solid ${C.border}`}}>
        <button onClick={handleGenerate} disabled={loading}
          style={{width:"100%",padding:"16px",
            background:loading?C.faint:`linear-gradient(135deg,${C.accent},${C.accent2})`,
            border:"none",borderRadius:16,color:loading?C.muted:"white",
            fontWeight:900,fontSize:15,cursor:loading?"default":"pointer",letterSpacing:0.5}}>
          {loading?"Génération en cours…":"Générer le programme"}
        </button>
      </div>
    </div>
  );
}

function HomePage({programs,sessions,profile,streak,dataReady=true,onCreateProgram,onStartSession,onEditProgram,onDeleteProgram,selectedGroupName,onSelectGroup,customGroupNames,onAddCustomGroup,onDeleteGroup,onReorderGroup,onRenameGroup,progTab,onChangeProgTab,swipePage:swipePageProp,onChangeSwipePage,defaultProgramId,onSetDefault,onSaveProfile,onGenerateProgram,prs,newMedalKey,onClearMedal,totalXP=0,tutorialRefs,onAiAnalysis,fitnessGoal=null,freeAnalysesUsed=0,lastFreeAnalysisDate=null,onFreeAnalysisUsed,onApplySuggestions,C}) {
  const BtnC=mkBtn(C);
  const swipePage=swipePageProp??0;
  const setSwipePage=onChangeSwipePage||(()=>{});
  const setProgTab=onChangeProgTab; // alias vers le setter parent
  const [confirm,setConfirm]=useState(null);
  const [confirmDeleteGroup,setConfirmDeleteGroup]=useState(null);
  const [expanded,setExpanded]=useState({});
  const [showNewGroupModal,setShowNewGroupModal]=useState(false);
  const [showParamsModal,setShowParamsModal]=useState(false);
  const [showNextAll,setShowNextAll]=useState(false);
  const [newGroupName,setNewGroupName]=useState("");
  const [showAiAccordion,setShowAiAccordion]=useState(false);
  const [aiLoading,setAiLoading]=useState(false);
  const [aiAppliedLocal,setAiAppliedLocal]=useState(false);
  // TODO: remplacer par isPremium réel quand Stripe sera branché
  const isPremium=false;
  // Logique analyses gratuites (utilisée quand isPremium===false)
  const FREE_ANALYSIS_MAX = 3;
  const FREE_ANALYSIS_DELAY_DAYS = 7;
  const analysesExhausted = freeAnalysesUsed >= FREE_ANALYSIS_MAX;
  const lastDate = lastFreeAnalysisDate ? new Date(lastFreeAnalysisDate) : null;
  const daysSinceLast = lastDate ? (Date.now() - lastDate.getTime()) / (1000*60*60*24) : Infinity;
  const analysisOnCooldown = !analysesExhausted && daysSinceLast < FREE_ANALYSIS_DELAY_DAYS;
  const nextAvailableDate = analysisOnCooldown && lastDate
    ? new Date(lastDate.getTime() + FREE_ANALYSIS_DELAY_DAYS*24*60*60*1000).toLocaleDateString("fr-FR",{day:"numeric",month:"long"})
    : null;
  const canAnalyzeFree = !analysesExhausted && !analysisOnCooldown;
  const [dayPopup,setDayPopup]=useState(null);
  const [editGroupModal, setEditGroupModal] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [dragOrder, setDragOrder] = useState([]);
  const dragSrcRef = useRef(null);
  const dragFloatRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragRowHeightRef = useRef(56);
  const touchStartX=useRef(null);
  const touchStartY=useRef(null);
  // Mascotte : message figé — recalculé uniquement quand dataReady devient true ou quand newMedalKey change
  const mascotMsgRef = useRef(null);
  const mascotReadyRef = useRef(false);
  const [todayStr, setTodayStr] = useState(() => new Date().toISOString().slice(0,10));
  // Réinitialisation à minuit
  useEffect(() => {
    const checkMidnight = setInterval(() => {
      const now = new Date().toISOString().slice(0,10);
      if(now !== todayStr) {
        mascotReadyRef.current = false;
        mascotMsgRef.current = null;
        try { localStorage.removeItem('bearforge_mascot_today'); } catch {}
        setTodayStr(now);
      }
    }, 30000); // vérif toutes les 30s
    return () => clearInterval(checkMidnight);
  }, [todayStr]);

  const muscuSessions=sessions.filter(s=>s.type==="muscu"||!s.type);

  // Groupes
  const generatedGroupNames=[...new Set(programs.filter(p=>!p.isCustom&&p.groupName).map(p=>p.groupName))];
  const allCustomGroupNames=[...new Set([...customGroupNames,...programs.filter(p=>p.isCustom&&p.groupName).map(p=>p.groupName)])];

  // Prochaine séance
  const activeGeneratedGroup=selectedGroupName&&generatedGroupNames.includes(selectedGroupName)?selectedGroupName:(generatedGroupNames[0]||null);
  const todayKey=(()=>{const d=new Date();return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;})();
  const doneToday=sessions.filter(s=>s.date&&s.date.slice(0,10)===todayKey).map(s=>s.programId);
  // Si un programme par défaut est défini, utiliser son groupe ; sinon groupe généré
  // defaultProgramId = nom du groupe actif (généré ou perso)
  const activeGroupName=defaultProgramId||activeGeneratedGroup;
  const activeGroup=programs.filter(p=>p.groupName===activeGroupName);
  // Rotation : trouver la dernière séance faite dans ce groupe, prendre la suivante dans l'ordre
  const groupSessions=sessions.filter(s=>activeGroup.some(p=>p.id===s.programId));
  const lastGroupSession=groupSessions.length>0?groupSessions[groupSessions.length-1]:null;
  const lastProgIdx=lastGroupSession?activeGroup.findIndex(p=>p.id===lastGroupSession.programId):-1;
  // nextProg calculé après dStart/localDateKey/missedProg (voir plus bas)
  const _nextProgIdxFallback=lastProgIdx>=0?(lastProgIdx+1)%activeGroup.length:0;
  const generatedProgs=programs.filter(p=>p.groupName===activeGeneratedGroup);

  // Durée estimée
  const estimateDuration=(prog)=>{
    if(!prog) return null;
    const totalSets=prog.exercises.reduce((a,e)=>a+(e.plannedSets?.length||0),0);
    const numExercises=prog.exercises.length||1;
    // Repos entre séries uniquement (pas après la dernière série de chaque exercice)
    const totalRest=prog.exercises.reduce((a,e)=>{
      const sets=e.plannedSets?.length||0;
      const restPerSet=e.restTime||90;
      return a+Math.max(0,sets-1)*restPerSet;
    },0);
    return Math.round((totalRest + totalSets*45)/60);
  };
  // estDuration et muscles définis après nextProg

  // Planning semaine
  const today=new Date();
  const todayIdx=today.getDay()===0?6:today.getDay()-1;
  const weekStart=new Date(today.getFullYear(),today.getMonth(),today.getDate()-todayIdx);
  const DAYS=["L","M","M","J","V","S","D"];
  const localDateKey=(date)=>{const d=new Date(date);return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;};
  const seancesDuJour=(dayOffset)=>{
    const d=new Date(weekStart);d.setDate(d.getDate()+dayOffset);
    const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    return sessions.filter(s=>s.date&&localDateKey(s.date)===key);
  };
  const target = (() => {
    if(!programs||!defaultProgramId) return profile?.days||3;
    // Cas custom : defaultProgramId est le groupName (string)
    const byGroup = programs.filter(p=>p.groupName===defaultProgramId);
    if(byGroup.length>0) return byGroup.length;
    // Cas généré : defaultProgramId est un id numérique
    const prog = programs.find(p=>p.id===defaultProgramId);
    if(!prog) return profile?.days||3;
    if(prog.days) return prog.days;
    const grp = prog.groupName||'';
    const match = grp.match(/(\d+)j$/);
    if(match) return parseInt(match[1]);
    if(grp) return programs.filter(p=>p.groupName===grp).length||1;
    return profile?.days||3;
  })();
  // Jours distincts avec séance cette semaine (pas nb total de séances)
  const dStart=`${weekStart.getFullYear()}-${String(weekStart.getMonth()+1).padStart(2,"0")}-${String(weekStart.getDate()).padStart(2,"0")}`;
  // Détection séance sautée cette semaine
  const groupSessionsThisWeek=groupSessions.filter(s=>s.date&&localDateKey(s.date)>=dStart&&localDateKey(s.date)<=todayKey);
  const programIdsDoneThisWeek=new Set(groupSessionsThisWeek.map(s=>s.programId));
  // Une séance est vraiment "sautée" seulement si une séance d'index supérieur a déjà été faite cette semaine
  const missedProg=groupSessionsThisWeek.length>0
    ? activeGroup.find(p=>{
        if(programIdsDoneThisWeek.has(p.id)) return false;
        const idx=activeGroup.indexOf(p);
        return activeGroup.slice(idx+1).some(later=>programIdsDoneThisWeek.has(later.id));
      }) || null
    : null;
  const isMissedSession=!!missedProg;
  const nextProgIdx=missedProg ? activeGroup.indexOf(missedProg) : _nextProgIdxFallback;
  const nextProg=activeGroup[nextProgIdx]||activeGroup[0]||null;
  const estDuration=estimateDuration(nextProg);
  const muscles=[...new Set((nextProg?.exercises||[]).map(e=>e.muscle).filter(Boolean))];
  const doneThisWeek=new Set(
    sessions
      .filter(s=>s.date&&s.type!=='tabata'&&localDateKey(s.date)>=dStart&&localDateKey(s.date)<=todayKey)
      .map(s=>localDateKey(s.date))
  ).size;
  const dayStates=DAYS.map((_,i)=>{
    const seances=seancesDuJour(i);
    return {hasSes:seances.length>0,isToday:i===todayIdx,isFuture:i>todayIdx,seances};
  });

  // Swipe handlers
  const onTouchStart=e=>{touchStartX.current=e.touches[0].clientX;touchStartY.current=e.touches[0].clientY;};
  const onTouchEnd=e=>{
    if(touchStartX.current===null)return;
    const dx=e.changedTouches[0].clientX-touchStartX.current;
    const dy=Math.abs(e.changedTouches[0].clientY-touchStartY.current);
    if(Math.abs(dx)>50&&Math.abs(dx)>dy*1.5){
      if(dx<0&&swipePage===0)setSwipePage(1);
      if(dx>0&&swipePage===1)setSwipePage(0);
    }
    touchStartX.current=null;touchStartY.current=null;
  };

  const customGroups=allCustomGroupNames;

  return(
    <div style={{position:"fixed",inset:0,top:0,background:C.bg,display:"flex",flexDirection:"column",overflow:"hidden",zIndex:1}}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>

      {/* ── RAIL HORIZONTAL ── */}
      <div style={{flex:1,display:"flex",transform:`translateX(${swipePage*-50}%)`,transition:"transform 0.3s cubic-bezier(0.4,0,0.2,1)",width:"200%",minHeight:0,overflow:"hidden"}}>

        {/* ═══════════════════════════════════════════════════
            ÉCRAN 1 — Séance suivante + planning semaine
        ═══════════════════════════════════════════════════ */}
        <div style={{width:"50%",display:"flex",flexDirection:"column",boxSizing:"border-box",paddingTop:"calc(env(safe-area-inset-top) + 20px)",paddingBottom:"calc(env(safe-area-inset-bottom) + 80px)",overflowY:"auto",overflowX:"hidden",position:"relative",WebkitOverflowScrolling:"touch"}}>

          {/* ══ BADGE NIVEAU ══ */}
          {dataReady&&(()=>{
            const lvl = getLevelFromXP(totalXP);
            return (
              <div style={{display:"flex",justifyContent:"flex-end",paddingRight:16,marginBottom:4,flexShrink:0}}>
                <span style={{color:C.accent,fontSize:11,fontWeight:800,letterSpacing:0.5,opacity:0.85}}>Niv. {lvl}</span>
              </div>
            );
          })()}

          {/* ══ MASCOTTE + BULLE ══ */}
          {dataReady&&(()=>{
            const firstName = profile?.name?.split(" ")[0] || "";
            const MUSCLE_KEY_MAP = {
              "Pectoraux":"pectoraux","Dos":"dos","Épaules":"epaules","Biceps":"biceps",
              "Triceps":"triceps","Quadriceps":"jambes","Ischio-jambiers":"jambes",
              "Fessiers":"fessiers","Mollets":"jambes","Abdominaux":"abdos","Trapèzes":"dos",
            };
            const mainMuscle = MUSCLE_KEY_MAP[muscles[0]] || muscles[0]?.toLowerCase() || "";
            const now = new Date();
            const nowStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
            const yest = new Date(now); yest.setDate(yest.getDate()-1);
            const yesterdayStr = `${yest.getFullYear()}-${String(yest.getMonth()+1).padStart(2,'0')}-${String(yest.getDate()).padStart(2,'0')}`;
            // Recalculer seulement : première fois que dataReady=true, ou nouvelle médaille, ou nouvelle session aujourd'hui
            const muscuCountToday = muscuSessions.filter(s=>s.date&&(localDateKey(s.date)===nowStr||s.date.slice(0,10)===nowStr)).length;
            // Persistance du message du jour via localStorage
            const storedMascot = (() => {
              try { return JSON.parse(localStorage.getItem('bearforge_mascot_today') || 'null'); } catch { return null; }
            })();
            // localStorage prioritaire : si message du jour présent, on le réutilise toujours
            // sauf si nouvelle médaille ou nouvelle séance aujourd'hui (muscuCountToday a changé)
            const storedIsToday = storedMascot?.date === nowStr && !newMedalKey && storedMascot?._muscuCountToday === muscuCountToday;
            if(storedIsToday) {
              // Toujours restocker dans le ref (même si le composant a été démonté/remonté)
              mascotReadyRef.current = true;
              mascotMsgRef.current = storedMascot;
            }
            const needsRecalc = !storedIsToday && (!mascotReadyRef.current || !!newMedalKey || (mascotMsgRef.current?._muscuCountToday !== muscuCountToday));
            if(!needsRecalc && mascotMsgRef.current) {
              const msg = mascotMsgRef.current;
              return (
                <div style={{display:"flex",alignItems:"flex-end",padding:"0 16px",gap:10,flexShrink:0}}>
                  <img src={MASCOT_BUST_DATA} alt="" style={{width:88,flexShrink:0,filter:C.isDark?"drop-shadow(0 4px 14px rgba(0,0,0,0.3))":"drop-shadow(0 4px 14px rgba(0,0,0,0.15))"}}/>
                  <div style={{flex:1,position:"relative",background:`${C.card}EE`,border:`1.5px solid ${C.accent}40`,borderRadius:"14px 14px 14px 4px",padding:"8px 11px",marginBottom:6,backdropFilter:"blur(8px)"}}>
                    <div style={{position:"absolute",left:-7,bottom:10,width:0,height:0,borderTop:"6px solid transparent",borderBottom:"6px solid transparent",borderRight:`7px solid ${C.accent}40`}}/>
                    <div style={{position:"absolute",left:-5,bottom:11,width:0,height:0,borderTop:"5px solid transparent",borderBottom:"5px solid transparent",borderRight:`5px solid ${C.card}`}}/>
                    <p style={{color:C.text,fontSize:12,fontWeight:600,margin:0,lineHeight:1.4}}>{msg.message}</p>
                  </div>
                </div>
              );
            }

            // Séance faite aujourd'hui
            const sessionDoneToday = muscuSessions.some(s => s.date && (localDateKey(s.date) === nowStr || s.date.slice(0,10) === nowStr));

            // PR battu aujourd'hui — détection via prs (date du dernier enregistrement)
            const newPRToday = prs
              ? Object.values(prs).some(pr => pr?.date && pr.date.slice(0, 10) === nowStr)
              : false;
            // Nom de l'exercice PR pour le message
            const prExerciseToday = prs
              ? Object.entries(prs).find(([,pr]) => pr?.date && pr.date.slice(0, 10) === nowStr)?.[0] || ""
              : "";

            // Tonnage : comparer dernière séance muscu vs avant-dernière
            const lastSes = muscuSessions.at(-1);
            const prevSes = muscuSessions.length >= 2 ? muscuSessions.at(-2) : null;
            const calcTonnage = s => (s?.exercises||[]).reduce((a, ex) => a + (ex.sets||[]).reduce((b, st) => b + (st.reps||0)*(st.weight||0), 0), 0);
            const lastTonnage = calcTonnage(lastSes);
            const prevTonnage = calcTonnage(prevSes);
            const tonnageUp5pct = prevTonnage > 0 && lastTonnage >= prevTonnage * 1.05 && lastSes?.date?.slice(0,10) === nowStr;

            // Streak — uniquement si le seuil a été atteint aujourd'hui
            // (la session d'hier n'avait pas encore ce niveau)
            const muscuSessionsSortedDesc = [...muscuSessions].sort((a,b)=>new Date(b.date)-new Date(a.date));
            const sessionsBeforeToday = muscuSessionsSortedDesc.filter(s => s.date && localDateKey(s.date) < nowStr);
            const streakYesterday = sessionsBeforeToday.length > 0
              ? (() => {
                  const sorted = sessionsBeforeToday.map(s=>localDateKey(s.date)).sort().reverse();
                  let count = 0; let cur = yesterdayStr;
                  for(const d of sorted){ if(d===cur){count++;const p=new Date(cur);p.setDate(p.getDate()-1);cur=`${p.getFullYear()}-${String(p.getMonth()+1).padStart(2,'0')}-${String(p.getDate()).padStart(2,'0')}`;} else if(d<cur) break; }
                  return count;
                })()
              : 0;
            const streak7 = streak >= 7 && streakYesterday < 7;
            const streak30 = streak >= 30 && streakYesterday < 30;

            // Tabata : incitation si muscu de la semaine complète mais pas de tabata cette semaine
            const tabataSessionsThisWeek = sessions.filter(s => s.type === 'tabata' && s.date && localDateKey(s.date) >= dStart && localDateKey(s.date) <= nowStr);
            const tabataToday = tabataSessionsThisWeek.some(s => localDateKey(s.date) === nowStr);
            const tabataIncitation = doneThisWeek >= target && tabataSessionsThisWeek.length === 0 && !sessionDoneToday;
            const tabataBonusDone = tabataToday && !sessionDoneToday;
            // Jour de repos : semaine muscu complète + tabata fait un jour précédent + pas de séance aujourd'hui
            const restDay = doneThisWeek >= target && tabataSessionsThisWeek.length > 0 && !tabataToday && !sessionDoneToday;

            // Jours depuis dernière séance
            const daysSinceLast = lastSes ? Math.floor((now - new Date(lastSes.date)) / 86400000) : 99;

            // Première connexion de la semaine (lundi ou pas de session cette semaine encore)
            const isFirstConnectionOfWeek = now.getDay() === 1 || doneThisWeek === 0;

            // Médaille — consommée une seule fois, puis effacée
            const medalKey = newMedalKey || null;
            if(medalKey && onClearMedal) setTimeout(onClearMedal, 0);

            let msg;
            try {
              const isFirstSessionEver = muscuSessions.length === 1 && sessionDoneToday;
              const firstSes = muscuSessions[0] || null;
              const firstSessionTonnage = firstSes ? calcTonnage(firstSes) : 0;
              const totalTonnage = muscuSessions.reduce((acc, s) => acc + calcTonnage(s), 0);
              msg = getMascotMessage({
                prenom: firstName,
                sessionMuscleGroup: mainMuscle,
                daysSinceLastSession: daysSinceLast,
                newPR: newPRToday,
                prExercise: prExerciseToday,
                weekCompleted: doneThisWeek >= target,
                isFirstConnectionOfWeek,
                sessionDoneToday,
                firstSessionEver: isFirstSessionEver,
                isAbsoluteFirstLogin: muscuSessions.length === 0,
                newMedalUnlocked: !!medalKey,
                newMedalKey: medalKey,
                tonnageUp5pct,
                stagnation: false,
                streak7,
                streak30,
                tabataIncitation,
                tabataBonusDone,
                restDay,
                sessionCount: muscuSessions.length,
                currentLevel: getLevelFromXP(totalXP),
                isPremium: false,
                firstSessionTonnage,
                lastSessionTonnage: lastTonnage,
                totalTonnage,
                isMissedSession,
                missedSessionName: missedProg?.name || "",
              });
            } catch {
              msg = { message: '', category: 'fallback' };
            }
            // Figer le message
            mascotReadyRef.current = true;
            mascotMsgRef.current = { ...msg, _muscuCountToday: muscuCountToday };
            try { localStorage.setItem('bearforge_mascot_today', JSON.stringify({ ...msg, _muscuCountToday: muscuCountToday, date: nowStr })); } catch {}
            return (
              <div style={{display:"flex",alignItems:"flex-end",padding:"0 16px",gap:10,flexShrink:0}}>
                <img src={MASCOT_BUST_DATA} alt="" style={{width:88,flexShrink:0,filter:C.isDark?"drop-shadow(0 4px 14px rgba(0,0,0,0.3))":"drop-shadow(0 4px 14px rgba(0,0,0,0.15))"}}/>
                <div style={{flex:1,position:"relative",background:`${C.card}EE`,border:`1.5px solid ${C.accent}40`,borderRadius:"14px 14px 14px 4px",padding:"8px 11px",marginBottom:6,backdropFilter:"blur(8px)"}}>
                  <div style={{position:"absolute",left:-7,bottom:10,width:0,height:0,borderTop:"6px solid transparent",borderBottom:"6px solid transparent",borderRight:`7px solid ${C.accent}40`}}/>
                  <div style={{position:"absolute",left:-5,bottom:11,width:0,height:0,borderTop:"5px solid transparent",borderBottom:"5px solid transparent",borderRight:`5px solid ${C.card}`}}/>
                  <p style={{color:C.text,fontSize:12,fontWeight:600,margin:0,lineHeight:1.4}}>{msg.message}</p>
                </div>
              </div>
            );
          })()}

          {/* ══ CARTE SÉANCE TERMINÉE AUJOURD'HUI ══ */}
          {(()=>{
            const todaySessions=sessions.filter(s=>s.date&&s.date.slice(0,10)===todayKey&&(s.type==="muscu"||!s.type));
            if(!todaySessions.length) return null;
            const s=todaySessions[todaySessions.length-1];
            const vol=(s.exercises||[]).reduce((a,ex)=>a+(ex.sets||[]).reduce((b,st)=>b+(st.reps||0)*(st.weight||0),0),0);
            const sessionMuscles=[...new Set((s.exercises||[]).map(e=>e.muscle).filter(Boolean))];
            const userWeight=profile?.weight||null;
            let kcalStr=null;
            if(userWeight&&s.duration>0){
              const avgW=(s.exercises||[]).reduce((a,ex)=>a+(ex.sets||[]).reduce((b,st)=>b+(st.weight||0),0),0)/
                Math.max(1,(s.exercises||[]).reduce((a,ex)=>a+(ex.sets||[]).length,0));
              const met=avgW>=60?6:avgW>=20?5:3.5;
              kcalStr=`~${Math.round(met*userWeight*(s.duration/60))} kcal`;
            }
            return(
              <div style={{flexShrink:0,padding:"8px 16px 0"}}>
                <div style={{background:`${C.card}`,border:`1.5px solid ${C.accent}50`,borderRadius:16,padding:"12px 14px"}}>
                  <p style={{color:C.accent,fontSize:8,letterSpacing:2,fontWeight:700,margin:"0 0 6px",opacity:0.8}}>SÉANCE TERMINÉE AUJOURD'HUI ✓</p>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                    <p style={{color:C.text,fontWeight:800,fontSize:14,margin:0,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.programName}</p>
                    <p style={{color:C.muted,fontSize:12,fontWeight:600,margin:"0 0 0 8px",flexShrink:0}}>{s.duration}min</p>
                  </div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:4}}>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                      {sessionMuscles.slice(0,3).map(m=>(
                        <span key={m} style={{background:`${C.accent}18`,color:C.accent,fontSize:10,fontWeight:700,borderRadius:8,padding:"2px 8px"}}>{m}</span>
                      ))}
                    </div>
                    {kcalStr&&<span style={{color:C.muted,fontSize:11,fontWeight:600}}>{kcalStr}</span>}
                  </div>

                  {/* Accordéon Beaggy IA */}
                  {isPremium&&(
                    <div style={{marginTop:10,borderTop:`1px solid ${C.border}`,paddingTop:8}}>
                      <button onClick={()=>setShowAiAccordion(v=>!v)}
                        style={{width:"100%",background:"none",border:"none",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",padding:0}}>
                        <span style={{color:C.accent,fontSize:11,fontWeight:700}}>✦ Analyse Beaggy IA</span>
                        <span style={{color:C.muted,fontSize:13}}>{showAiAccordion?"▲":"▼"}</span>
                      </button>
                      {showAiAccordion&&(
                        <div style={{marginTop:8}}>
                          {s.aiAnalysis ? (
                            <>
                            {(()=>{
                              const a=s.aiAnalysis;
                              const sections=[{key:"bilan",label:"Bilan"},{key:"pointFort",label:"Point fort"},{key:"suggestion",label:"Prochaine séance"},{key:"variante",label:"Variante"}];
                              if(typeof a==="object"&&a!==null){
                                return sections.filter(sc=>a[sc.key]&&a[sc.key]!==null).map((sc,i)=>(
                                  <div key={sc.key} style={{marginTop:i===0?0:8}}>
                                    <p style={{color:C.accent,fontSize:11,fontWeight:700,margin:"0 0 2px"}}>· {sc.label}</p>
                                    <p style={{color:C.text,fontSize:13,lineHeight:1.55,margin:0,paddingLeft:10}}>{a[sc.key]}</p>
                                  </div>
                                ));
                              }
                              return <p style={{color:C.text,fontSize:13,lineHeight:1.6,margin:0}}>{String(a)}</p>;
                            })()}
                            {s.aiAnalysis&&typeof s.aiAnalysis==="object"&&s.aiAnalysis.adaptations?.length>0&&(
                              (s.aiApplied||aiAppliedLocal) ? (
                                <p style={{color:C.muted,fontSize:11,margin:"10px 0 0",textAlign:"center"}}>✓ Modifications appliquées à la prochaine séance</p>
                              ) : (
                                <div style={{marginTop:10}}>
                                  <p style={{color:C.accent,fontSize:9,letterSpacing:2,fontWeight:700,margin:"0 0 8px"}}>SUGGESTIONS POUR LA PROCHAINE SÉANCE</p>
                                  {s.aiAnalysis.adaptations.map((a,i)=>(
                                    <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",background:C.faint,borderRadius:9,marginBottom:5,border:`1px solid ${C.border}`}}>
                                      <div style={{flex:1,minWidth:0}}>
                                        <p style={{color:C.text,fontSize:12,fontWeight:700,margin:"0 0 1px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.exercise}</p>
                                        <p style={{color:C.muted,fontSize:10,margin:0}}>{a.reason}</p>
                                      </div>
                                      <div style={{flexShrink:0,textAlign:"right",marginLeft:10}}>
                                        <p style={{color:C.accent,fontSize:12,fontWeight:800,margin:0}}>{a.sets}×{a.reps} reps</p>
                                        <p style={{color:C.muted,fontSize:10,margin:0}}>{a.weight>0?`${a.weight} kg`:"poids libre"}</p>
                                      </div>
                                    </div>
                                  ))}
                                  <button onClick={()=>{if(onApplySuggestions)onApplySuggestions(s.id,s.aiAnalysis.adaptations);setAiAppliedLocal(true);}}
                                    style={{width:"100%",marginTop:6,padding:"10px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:10,color:"white",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                                    Appliquer à la prochaine séance
                                  </button>
                                </div>
                              )
                            )}
                            </>
                          ) : aiLoading ? (
                            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0"}}>
                              <span style={{display:"inline-block",width:14,height:14,border:`2px solid ${C.accent}40`,borderTop:`2px solid ${C.accent}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
                              <span style={{color:C.muted,fontSize:13}}>Beaggy analyse ta séance...</span>
                            </div>
                          ) : isPremium || canAnalyzeFree ? (
                            <div style={{display:"flex",flexDirection:"column",gap:4}}>
                              <button onClick={async()=>{
                                setAiLoading(true);
                                try{
                                  const exs=s.exercises||[];
                                  const vol2=exs.reduce((a,ex)=>a+(ex.sets||[]).reduce((b,st)=>b+(st.reps||0)*(st.weight||0),0),0);
                                  const userPrompt=`Séance : ${s.programName}\nDurée : ${s.duration} min\nVolume total : ${vol2} kg\nExercices :\n${exs.map(ex=>{const maxW=Math.max(0,...(ex.sets||[]).map(st=>st.weight||0));const totalReps=(ex.sets||[]).reduce((a,st)=>a+(st.reps||0),0);return `- ${ex.name} : ${(ex.sets||[]).length} séries, ${totalReps} reps, charge max ${maxW}kg`;}).join("\n")}`;
                                  const resp=await fetch("/api/beaggy",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userPrompt,fitnessGoal})});
                                  const d=await resp.json();
                                  const result=d.analysis||d.text||"Analyse indisponible.";
                                  if(onAiAnalysis) onAiAnalysis(s.id,result);
                                  if(!isPremium && onFreeAnalysisUsed) onFreeAnalysisUsed();
                                }catch(e){
                                  if(onAiAnalysis) onAiAnalysis(s.id, navigator.onLine
                                    ? "⚠️ IA indisponible pour le moment — réessaie plus tard."
                                    : "⚠️ IA indisponible — vérifie ta connexion internet.");
                                }
                                setAiLoading(false);
                              }}
                                style={{width:"100%",padding:"10px",background:"none",border:`1px solid ${C.accent}50`,borderRadius:10,color:C.accent,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                                Générer l'analyse →
                              </button>
                              {!isPremium&&<p style={{margin:0,textAlign:"center",fontSize:11,color:C.muted,fontWeight:600}}>{freeAnalysesUsed}/{FREE_ANALYSIS_MAX} analyses gratuites utilisées</p>}
                            </div>
                          ) : analysisOnCooldown ? (
                            <div style={{display:"flex",flexDirection:"column",gap:4}}>
                              <button disabled style={{width:"100%",padding:"10px",background:"none",border:`1px solid ${C.border}`,borderRadius:10,color:C.muted,fontWeight:700,fontSize:13,cursor:"default",opacity:0.6}}>
                                ✦ Analyse Beaggy IA 🔒
                              </button>
                              <p style={{margin:0,textAlign:"center",fontSize:11,color:C.muted,fontWeight:600}}>{freeAnalysesUsed}/{FREE_ANALYSIS_MAX} analyses gratuites — prochaine le {nextAvailableDate}</p>
                            </div>
                          ) : (
                            <div style={{display:"flex",flexDirection:"column",gap:4}}>
                              <button disabled style={{width:"100%",padding:"10px",background:"none",border:`1px solid ${C.border}`,borderRadius:10,color:C.muted,fontWeight:700,fontSize:13,cursor:"default",opacity:0.6}}>
                                ✦ Analyse Beaggy IA 🔒
                              </button>
                              <p style={{margin:0,textAlign:"center",fontSize:11,color:C.muted,fontWeight:600}}>3/3 analyses gratuites utilisées — <span style={{color:C.accent,fontWeight:700}}>Passer Premium →</span></p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ══ CARTE SÉANCE SUIVANTE — hauteur naturelle ══ */}
          <div style={{flexShrink:0,padding:"8px 16px 0"}}>
            <div style={{background:`linear-gradient(145deg,${C.accent}18,${C.accent2}0A)`,border:`1.5px solid ${C.accent}30`,borderRadius:18,overflow:"hidden"}}>

              {/* Header */}
              <div style={{padding:"10px 14px 6px"}}>
                {/* Ligne 1 : PROCHAINE SÉANCE + programme favori */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                  <p style={{color:C.accent,fontSize:8,letterSpacing:2,fontWeight:700,margin:0,opacity:0.8}}>PROCHAINE SÉANCE</p>
                  {defaultProgramId&&<span style={{color:C.accent,fontSize:9,fontWeight:700,letterSpacing:1}}>★ {defaultProgramId}</span>}
                </div>
                {/* Ligne 2 : nom séance + muscles + durée */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <h2 style={{color:C.text,fontSize:15,fontWeight:900,margin:0,lineHeight:1.2,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {nextProg?.name||"Aucun programme"}
                  </h2>
                  <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0,marginLeft:8}}>
                    {muscles.slice(0,2).map((m,i)=>(
                      <span key={i} style={{background:`${C.accent}12`,color:C.accent,borderRadius:20,padding:"2px 7px",fontSize:9,fontWeight:700}}>{m}</span>
                    ))}
                    {estDuration&&<span style={{color:C.muted,fontSize:10,fontWeight:600}}>{estDuration}min</span>}
                  </div>
                </div>
              </div>

              {/* Liste exercices — progressive disclosure */}
              {nextProg&&(
                <div style={{borderTop:`1px solid ${C.accent}18`}}>
                  <div style={{padding:"0 14px"}}>
                    {(showNextAll?nextProg.exercises:nextProg.exercises.slice(0,3)).map((ex,i,arr)=>{
                      const meta=MM[ex.muscle]||{};
                      return(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 0",borderBottom:i<(arr.length-1)?`1px solid ${C.border}30`:"none"}}>
                          <div style={{width:5,height:5,borderRadius:"50%",background:meta.color||C.accent,flexShrink:0}}/>
                          <span style={{color:C.text,fontSize:12,fontWeight:700,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ex.name}</span>
                          <span style={{color:meta.color||C.muted,fontSize:9,fontWeight:700,flexShrink:0,minWidth:38,textAlign:"right"}}>{ex.muscle}</span>
                        </div>
                      );
                    })}
                  </div>
                  {nextProg.exercises.length>3&&(
                    <button onClick={()=>setShowNextAll(v=>!v)}
                      style={{width:"100%",padding:"5px 14px",background:"none",border:"none",color:C.muted,fontSize:11,fontWeight:700,cursor:"pointer",textAlign:"center",borderTop:`1px solid ${C.border}20`}}>
                      {showNextAll?`▲ Voir moins`:`▼ Voir les ${nextProg.exercises.length-3} exercices suivants`}
                    </button>
                  )}
                  <TonnageChart sessions={sessions} progId={nextProg.id} C={C}/>
                </div>
              )}

              {/* Bouton */}
              <div style={{padding:"7px 14px 9px"}}>
                {nextProg?(
                  <button ref={tutorialRefs?.startSession} onClick={()=>{if(navigator?.vibrate){try{navigator.vibrate([30,50,30]);}catch{}}onStartSession(nextProg);}}
                    style={{width:"100%",padding:"9px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:10,color:"white",fontWeight:800,fontSize:13,cursor:"pointer",boxShadow:`0 3px 12px ${C.glow}`}}>
                    Commencer →
                  </button>
                ):(
                  <button onClick={()=>setSwipePage(1)}
                    style={{width:"100%",padding:"9px",background:"transparent",border:`1.5px dashed ${C.accent}`,borderRadius:10,color:C.accent,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                    Choisir un programme →
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Semainier inline — visible uniquement quand carte dépliée */}
          {(showNextAll||showAiAccordion)&&(
            <div style={{flexShrink:0,padding:"8px 16px 12px",position:"relative"}}
              onClick={()=>setDayPopup(null)}>
        {dayPopup&&(
          <div style={{position:"absolute",top:-48,left:10,right:10,background:C.card,border:`1px solid ${C.accent}40`,borderRadius:10,padding:"5px 10px",zIndex:15,boxShadow:"0 -4px 16px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
            {dayPopup.map((s,i)=><p key={i} style={{color:C.text,fontWeight:700,fontSize:11,margin:"1px 0"}}>{s.programName}</p>)}
          </div>
        )}
        <div style={{display:"flex",gap:5,justifyContent:"center"}}>
          {dayStates.map(({hasSes,isToday,seances},i)=>(
            <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,width:30}}
              onClick={hasSes?e=>{e.stopPropagation();setDayPopup(dayPopup?null:seances);}:undefined}>
              <span style={{fontSize:8,fontWeight:isToday?800:400,color:isToday?C.accent:C.muted}}>{DAYS[i]}</span>
              <div style={{
                width:24,height:24,borderRadius:6,
                background:hasSes?`${C.accent}25`:isToday?`${C.accent}15`:`${C.border}40`,
                border:`1px solid ${hasSes?C.accent+"80":isToday?C.accent+"40":C.border+"50"}`,
                display:"flex",alignItems:"center",justifyContent:"center",
                cursor:hasSes?"pointer":"default"
              }}>
                {hasSes&&<span style={{fontSize:9,color:C.accent,fontWeight:900}}>✓</span>}
                {isToday&&!hasSes&&<span style={{fontSize:6,color:C.accent,opacity:0.7}}>●</span>}
              </div>
            </div>
          ))}
        </div>
            </div>
          )}
          {/* Espace bas quand les deux cartes repliées (évite que contenu passe sous semainier fixe) */}
          {!showNextAll&&!showAiAccordion&&<div style={{height:60}}/>}

        </div>

        {/* ═══════════════════════════════════════════════════
            ÉCRAN 2 — Sélection programmes
        ═══════════════════════════════════════════════════ */}
        <div style={{width:"50%",display:"flex",flexDirection:"column",paddingTop:"calc(env(safe-area-inset-top) + 20px)",paddingBottom:"calc(env(safe-area-inset-bottom) + 110px)",boxSizing:"border-box",overflow:"hidden"}}>
          <p style={{color:C.muted,fontSize:10,letterSpacing:3,fontWeight:700,margin:"0 0 10px",padding:"0 20px"}}>PROGRAMMES</p>

          {/* Onglets Générés | Perso */}
          <div style={{display:"flex",gap:0,margin:"0 20px 12px",background:C.faint,borderRadius:12,padding:3,border:`1px solid ${C.border}`}}>
            <button ref={tutorialRefs?.tabGenerated} onClick={()=>setProgTab("generated")}
              style={{flex:1,padding:"7px",borderRadius:9,border:"none",background:progTab==="generated"?C.card:"transparent",color:progTab==="generated"?C.text:C.muted,fontWeight:progTab==="generated"?800:500,fontSize:12,cursor:"pointer",transition:"all 0.2s"}}>
              Générés
            </button>
            <button ref={tutorialRefs?.tabCustom} onClick={()=>setProgTab("custom")}
              style={{flex:1,padding:"7px",borderRadius:9,border:"none",background:progTab==="custom"?C.card:"transparent",color:progTab==="custom"?C.text:C.muted,fontWeight:progTab==="custom"?800:500,fontSize:12,cursor:"pointer",transition:"all 0.2s"}}>
              Perso
            </button>
          </div>

          {/* Liste scrollable */}
          <div style={{flex:1,overflowY:"auto",padding:"0 20px",paddingBottom:120}}>
            {progTab==="generated"?(
              <>
                {generatedGroupNames.length===0?(
                  <div style={{background:C.card,borderRadius:18,padding:"32px 20px",textAlign:"center",border:`1px dashed ${C.border}`}}>
                    <p style={{color:C.muted,fontSize:14,margin:"0 0 16px"}}>Aucun programme généré</p>
                    <button onClick={()=>onGenerateProgram&&onGenerateProgram()} style={{padding:"11px 24px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,color:"white",fontWeight:800,fontSize:14,cursor:"pointer"}}>
                      Générer mon programme
                    </button>
                  </div>
                ):(
                  <>
                    <div style={{display:"flex",flexDirection:"column",gap:9}}>
                      {generatedGroupNames.map((g,gIdx)=>{
                        const gProgs=programs.filter(p=>p.groupName===g);
                        const isActive=defaultProgramId===g;
                        return(
                          <div key={g} style={{background:C.card,borderRadius:18,border:`1.5px solid ${isActive?C.accent:C.border}`,overflow:"hidden"}}>
                            {/* Header groupe */}
                            <div style={{padding:"12px 14px 10px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                              <div>
                                <p style={{color:C.text,fontWeight:800,fontSize:15,margin:"0 0 2px"}}>{g}</p>
                                <p style={{color:C.muted,fontSize:11,margin:"0 0 4px"}}>{gProgs.length} séance{gProgs.length!==1?"s":""}</p>
                                <button ref={gIdx===0?tutorialRefs?.setActive:null} onClick={e=>{e.stopPropagation();onSetDefault(g);}}
                                  style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:20,border:`1.5px solid ${isActive?C.accent:C.border}`,background:isActive?`${C.accent}15`:"none",cursor:"pointer",marginTop:2}}>
                                  <span style={{fontSize:13,color:isActive?C.accent:C.muted}}>★</span>
                                  <span style={{fontSize:12,color:isActive?C.accent:C.muted,fontWeight:isActive?700:500}}>{isActive?"Programme actif":"Définir comme actif"}</span>
                                </button>
                              </div>
                            </div>
                            {/* Séances du groupe */}
                            {gProgs.length>0&&(
                              <div style={{borderTop:`1px solid ${C.border}`,padding:"8px 14px 12px",display:"flex",flexDirection:"column",gap:7}}>
                                {gProgs.map(prog=>{
                                  const tot=prog.exercises.reduce((a,e)=>a+(e.plannedSets?.length||0),0);
                                  const est=estimateDuration(prog);
                                  const isOpen=!!expanded[prog.id];
                                  return(
                                    <div key={prog.id} style={{background:C.faint,borderRadius:12,border:`1px solid ${C.border}`}}>
                                      <div style={{padding:"10px 12px",display:"flex",alignItems:"center",gap:8}}>
                                        <button onClick={()=>setExpanded(p=>({...p,[prog.id]:!p[prog.id]}))}
                                          style={{background:"none",border:"none",color:C.muted,fontSize:18,cursor:"pointer",padding:0,lineHeight:1,transform:isOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s",flexShrink:0}}>▼</button>
                                        <div style={{flex:1,minWidth:0}}>
                                          <p style={{color:C.text,fontWeight:800,fontSize:15,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{prog.name}</p>
                                          <p style={{color:C.muted,fontSize:10,margin:0}}>{prog.exercises.length} exo · {tot} séries{est?` · ~${est}min`:""}</p>
                                        </div>
                                        <button onClick={()=>{if(navigator?.vibrate){try{navigator.vibrate([30,50,30]);}catch{}}onStartSession(prog);}}
                                          style={{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,padding:"10px 18px",color:"white",fontWeight:900,fontSize:15,cursor:"pointer",flexShrink:0}}>
                                          GO
                                        </button>
                                      </div>
                                      {isOpen&&(
                                        <div style={{borderTop:`1px solid ${C.border}`,padding:"8px 12px 10px",display:"flex",flexDirection:"column",gap:5}}>
                                          {prog.exercises.map((ex,i)=>{
                                            const meta=MM[ex.muscle]||{};
                                            return(
                                              <div key={i} style={{display:"flex",alignItems:"center",gap:7}}>
                                                <div style={{width:4,height:4,borderRadius:"50%",background:meta.color||C.muted,flexShrink:0}}/>
                                                <span style={{color:C.text,fontSize:12,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ex.name}</span>
                                                <span style={{color:C.muted,fontSize:10}}>{ex.muscle}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            ):(
              /* ── Onglet Perso ── */
              <>
                {customGroups.length===0?(
                  <div style={{background:C.card,borderRadius:18,padding:"32px 20px",textAlign:"center",border:`1px dashed ${C.border}`}}>
                    <p style={{color:C.muted,fontSize:14,margin:"0 0 16px"}}>Aucun programme perso</p>
                    <button onClick={()=>{setShowNewGroupModal(true);setNewGroupName("");}}
                      style={{padding:"11px 24px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,color:"white",fontWeight:800,fontSize:14,cursor:"pointer"}}>
                      + Créer un programme
                    </button>
                  </div>
                ):(
                  <>
                    <div style={{display:"flex",flexDirection:"column",gap:9,marginBottom:12}}>
                      {customGroups.map(g=>{
                        const groupProgs=programs.filter(p=>p.groupName===g);
                        const isActive=defaultProgramId===g;
                        return(
                          <div key={g} style={{background:C.card,borderRadius:18,border:`1.5px solid ${isActive?C.accent:C.border}`,overflow:"hidden"}}>
                            {/* Header groupe */}
                            <div style={{padding:"12px 14px 10px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                              <div>
                                <p style={{color:C.text,fontWeight:800,fontSize:15,margin:"0 0 2px"}}>{g}</p>
                                <p style={{color:C.muted,fontSize:11,margin:"0 0 4px"}}>{groupProgs.length} séance{groupProgs.length!==1?"s":""}</p>
                                <button onClick={e=>{e.stopPropagation();onSetDefault(g);}}
                                  style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:20,border:`1.5px solid ${defaultProgramId===g?C.accent:C.border}`,background:defaultProgramId===g?`${C.accent}15`:"none",cursor:"pointer",marginTop:2}}>
                                  <span style={{fontSize:13,color:defaultProgramId===g?C.accent:C.muted}}>★</span>
                                  <span style={{fontSize:12,color:defaultProgramId===g?C.accent:C.muted,fontWeight:defaultProgramId===g?700:500}}>{defaultProgramId===g?"Programme actif":"Définir comme actif"}</span>
                                </button>
                              </div>
                              <button onClick={e=>{e.stopPropagation();setEditGroupName(g);setEditGroupModal(g);}}
                               style={{display:"inline-flex",alignItems:"center",padding:"5px 11px",borderRadius:20,border:`1.5px solid ${C.border}`,background:"none",marginLeft:4}}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                              </button>
                            </div>
                            {/* Séances du groupe */}
                            {groupProgs.length>0&&(
                              <div style={{borderTop:`1px solid ${C.border}`,padding:"8px 14px 12px",display:"flex",flexDirection:"column",gap:7}}>
                                {groupProgs.map(prog=>{
                                  const est=estimateDuration(prog);
                                  const isOpen=!!expanded[prog.id];
                                  return(
                                    <div key={prog.id} style={{background:C.faint,borderRadius:12,border:`1px solid ${C.border}`}}>
                                      <div style={{padding:"10px 12px",display:"flex",alignItems:"center",gap:8}}>
                                        <button onClick={()=>setExpanded(p=>({...p,[prog.id]:!p[prog.id]}))}
                                          style={{background:"none",border:"none",color:C.muted,fontSize:18,cursor:"pointer",padding:0,lineHeight:1,transform:isOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s",flexShrink:0}}>▼</button>
                                        <div style={{flex:1,minWidth:0}}>
                                          <p style={{color:C.text,fontWeight:800,fontSize:15,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{prog.name}</p>
                                          <p style={{color:C.muted,fontSize:10,margin:0}}>{prog.exercises.length} exo{est?` · ~${est}min`:""}</p>
                                        </div>
                                        <button onClick={()=>{if(navigator?.vibrate){try{navigator.vibrate([30,50,30]);}catch{}}onStartSession(prog);}}
                                          style={{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,padding:"10px 18px",color:"white",fontWeight:900,fontSize:15,cursor:"pointer",flexShrink:0}}>
                                          GO
                                        </button>
                                      </div>
                                      {isOpen&&(
                                        <div style={{borderTop:`1px solid ${C.border}`,padding:"8px 12px 10px",display:"flex",flexDirection:"column",gap:5}}>
                                          {prog.exercises.map((ex,i)=>{
                                            const meta=MM[ex.muscle]||{};
                                            return(
                                              <div key={i} style={{display:"flex",alignItems:"center",gap:7}}>
                                                <div style={{width:4,height:4,borderRadius:"50%",background:meta.color||C.muted,flexShrink:0}}/>
                                                <span style={{color:C.text,fontSize:12,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ex.name}</span>
                                                <span style={{color:C.muted,fontSize:10}}>{ex.muscle}</span>
                                              </div>
                                            );
                                          })}
                                          <div style={{display:"flex",gap:7,marginTop:6}}>
                                            <button onClick={()=>onEditProgram(prog)}
                                              style={{flex:1,padding:"8px",background:C.card,border:`1px solid ${C.border}`,borderRadius:9,color:C.text,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                                              Modifier
                                            </button>
                                            <button onClick={()=>setConfirm(prog)}
                                              style={{flex:1,padding:"8px",background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:9,color:"#EF4444",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                                              Supprimer
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button ref={tutorialRefs?.createProg} onClick={()=>{setShowNewGroupModal(true);setNewGroupName("");}}
                      style={{width:"100%",padding:"13px",background:"transparent",border:`2px dashed ${C.accent}`,borderRadius:14,color:C.accent,fontWeight:700,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                      <span style={{fontSize:18,fontWeight:900}}>+</span> Nouveau programme
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          {/* Bouton fixe "Adapte ton programme" - visible uniquement onglet Généré avec programmes */}
          {progTab==="generated"&&(
            <div style={{padding:"10px 20px 12px",borderTop:`1px solid ${C.border}`,background:C.bg,flexShrink:0}}>
              <button onClick={()=>onGenerateProgram&&onGenerateProgram()}
                style={{width:"100%",padding:"12px 16px",background:`linear-gradient(135deg,${C.accent}22,${C.accent2}22)`,border:`1.5px solid ${C.accent}`,borderRadius:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",textAlign:"left"}}>
                <div>
                  <p style={{color:C.accent,fontWeight:700,fontSize:13,margin:"0 0 1px"}}>
                    {generatedGroupNames.length>0?"Regénérer mon programme":"Générer mon programme"}
                  </p>
                  <p style={{color:C.muted,fontSize:11,margin:0}}>Adapté à ton équipement et à ta fréquence</p>
                </div>
                <span style={{color:C.accent,fontSize:16,fontWeight:900}}>›</span>
              </button>
            </div>
          )}
        </div>
      </div>


      {editGroupModal&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:83,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"flex-end"}}
          onClick={()=>{setEditGroupModal(null);setDragOrder([]);}}>
          <div style={{background:C.card,borderRadius:"18px 18px 0 0",width:"100%",maxHeight:"calc(100% - 20px)",display:"flex",flexDirection:"column"}}
            onClick={e=>e.stopPropagation()}>

            {/* Poignée */}
            <div style={{display:"flex",justifyContent:"center",padding:"10px 0 4px"}}>
              <div style={{width:36,height:4,borderRadius:2,background:C.border}}/>
            </div>

            {/* Contenu scrollable */}
            <div style={{overflowY:"auto",padding:"0 16px",flex:1}}>
              <p style={{color:C.text,fontWeight:800,fontSize:16,margin:"12px 0 16px"}}>Modifier le programme</p>

              <p style={{color:C.muted,fontSize:12,margin:"0 0 6px"}}>Nom du programme</p>
              <input value={editGroupName} onChange={e=>setEditGroupName(e.target.value)}
                style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${C.border}`,
                  background:C.bg,color:C.text,fontSize:16,boxSizing:"border-box",marginBottom:16}}/>

              <p style={{color:C.muted,fontSize:12,margin:"0 0 8px"}}>Ordre des séances <span style={{fontSize:11,color:C.border}}>— maintenir et glisser</span></p>
              {(()=>{
                const baseProgs=programs.filter(p=>p.groupName===editGroupModal);
                const list=dragOrder.length===baseProgs.length?dragOrder.map(i=>baseProgs[i]):baseProgs;
                return list.map((prog,i)=>{
                  const isDragging=dragSrcRef.current===i;
                  return(
                    <div key={prog.id}
                      onTouchStart={e=>{
                        e.stopPropagation();
                        const rect=e.currentTarget.getBoundingClientRect();
                        dragSrcRef.current=i;
                        dragStartYRef.current=e.touches[0].clientY;
                        dragRowHeightRef.current=rect.height+8;
                        if(!dragOrder.length) setDragOrder(baseProgs.map((_,ii)=>ii));
                      }}
                      onTouchMove={e=>{
                        e.preventDefault();
                        e.stopPropagation();
                        const touchY=e.touches[0].clientY;
                        const dy=touchY-dragStartYRef.current;
                        const newIdx=Math.max(0,Math.min(list.length-1,Math.round(dragSrcRef.current+dy/dragRowHeightRef.current)));
                        if(newIdx!==dragSrcRef.current){
                          const cur=dragOrder.length?[...dragOrder]:baseProgs.map((_,ii)=>ii);
                          const[moved]=cur.splice(dragSrcRef.current,1);
                          cur.splice(newIdx,0,moved);
                          dragSrcRef.current=newIdx;
                          dragStartYRef.current=touchY;
                          setDragOrder([...cur]);
                        }
                      }}
                      onTouchEnd={()=>{
                        if(dragOrder.length){
                          const reordered=dragOrder.map(idx=>baseProgs[idx]);
                          onReorderGroup(editGroupModal,reordered);
                          setDragOrder([]);
                        }
                        dragSrcRef.current=null;
                      }}
                      style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,
                        background:isDragging?`${C.accent}18`:C.faint,
                        border:isDragging?`2px solid ${C.accent}60`:"2px solid transparent",
                        borderRadius:10,padding:"12px 14px",touchAction:"none",
                        transition:"background 0.1s,border 0.1s"}}>
                      <span style={{color:isDragging?C.accent:C.muted,fontSize:16,lineHeight:1}}>☰</span>
                      <span style={{flex:1,color:C.text,fontSize:14,fontWeight:600}}>{prog.name}</span>
                    </div>
                  );
                });
              })()}

              <button onClick={()=>{setEditGroupModal(null);setDragOrder([]);onCreateProgram(editGroupModal);}}
                style={{width:"100%",padding:"10px",background:`${C.accent}15`,border:`1px solid ${C.accent}40`,
                  borderRadius:10,color:C.accent,fontSize:13,fontWeight:600,marginTop:4,marginBottom:12}}>
                + Ajouter une séance
              </button>

              <button onClick={()=>{
                onDeleteGroup(editGroupModal);
                setEditGroupModal(null);setDragOrder([]);
              }} style={{padding:"8px 16px",background:"none",border:"none",
                color:"#ef4444",fontSize:13,fontWeight:600,marginBottom:8,display:"block",margin:"0 auto 8px"}}>
                Supprimer ce programme
              </button>
            </div>

            {/* Bouton Sauvegarder fixe en bas */}
            <div style={{padding:"12px 16px 16px",borderTop:`1px solid ${C.border}`,background:C.card,flexShrink:0}}>
              <button onClick={()=>{
                if(editGroupName.trim()&&editGroupName.trim()!==editGroupModal){
                  onRenameGroup(editGroupModal,editGroupName.trim());
                }
                setEditGroupModal(null);setDragOrder([]);
              }} style={{width:"100%",padding:"14px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,
                border:"none",borderRadius:12,color:"#fff",fontSize:15,fontWeight:700}}>
                Sauvegarder
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ══ SEMAINIER FIXE — carte repliée + page 0 uniquement ══ */}
      {!showNextAll&&!showAiAccordion&&swipePage===0&&(
        <div style={{position:"fixed",bottom:"calc(env(safe-area-inset-bottom) + 80px)",left:0,right:0,padding:"0 16px 8px",zIndex:10,background:C.bg}}
          onClick={()=>setDayPopup(null)}>
        {dayPopup&&(
          <div style={{position:"absolute",top:-48,left:10,right:10,background:C.card,border:`1px solid ${C.accent}40`,borderRadius:10,padding:"5px 10px",zIndex:15,boxShadow:"0 -4px 16px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
            {dayPopup.map((s,i)=><p key={i} style={{color:C.text,fontWeight:700,fontSize:11,margin:"1px 0"}}>{s.programName}</p>)}
          </div>
        )}
        <div style={{display:"flex",gap:5,justifyContent:"center"}}>
          {dayStates.map(({hasSes,isToday,seances},i)=>(
            <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,width:30}}
              onClick={hasSes?e=>{e.stopPropagation();setDayPopup(dayPopup?null:seances);}:undefined}>
              <span style={{fontSize:8,fontWeight:isToday?800:400,color:isToday?C.accent:C.muted}}>{DAYS[i]}</span>
              <div style={{
                width:24,height:24,borderRadius:6,
                background:hasSes?`${C.accent}25`:isToday?`${C.accent}15`:`${C.border}40`,
                border:`1px solid ${hasSes?C.accent+"80":isToday?C.accent+"40":C.border+"50"}`,
                display:"flex",alignItems:"center",justifyContent:"center",
                cursor:hasSes?"pointer":"default"
              }}>
                {hasSes&&<span style={{fontSize:9,color:C.accent,fontWeight:900}}>✓</span>}
                {isToday&&!hasSes&&<span style={{fontSize:6,color:C.accent,opacity:0.7}}>●</span>}
              </div>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* ── Indicateurs swipe (dots) ── */}
      <div style={{position:"fixed",bottom:106,left:"50%",transform:"translateX(-50%)",display:"flex",gap:8,zIndex:20,pointerEvents:"none"}}>
        {[0,1].map(i=>(
          <div key={i} style={{width:swipePage===i?28:8,height:8,borderRadius:4,background:swipePage===i?C.accent:C.border,transition:"all 0.3s",boxShadow:swipePage===i?`0 2px 8px ${C.glow}`:undefined}}/>
        ))}
      </div>

      {/* ── Modals ── */}
      {confirm&&<ConfirmModal message={`Supprimer "${confirm.name}" ?`} onConfirm={()=>{onDeleteProgram(confirm.id);setConfirm(null);}} onCancel={()=>setConfirm(null)} C={C}/>}

      {/* ── Modal paramètres programme généré ── */}
      {showParamsModal&&<ParamsModal profile={profile} onSave={p=>{if(onSaveProfile)onSaveProfile(p);}} onClose={()=>setShowParamsModal(false)} C={C}/>}

      {confirmDeleteGroup&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:"0 24px"}}>
          <div style={{background:C.card,borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:380,border:`1px solid ${C.border}`}}>
            <p style={{color:C.text,fontWeight:800,fontSize:17,margin:"0 0 8px",textAlign:"center"}}>Supprimer le programme ?</p>
            <p style={{color:C.muted,fontSize:14,margin:"0 0 6px",textAlign:"center",lineHeight:1.5}}><span style={{color:C.text,fontWeight:700}}>« {confirmDeleteGroup} »</span></p>
            <p style={{color:C.muted,fontSize:13,margin:"0 0 24px",textAlign:"center",lineHeight:1.5}}>Toutes les séances liées seront supprimées.</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <button onClick={()=>setConfirmDeleteGroup(null)} style={{padding:"12px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.muted,fontWeight:800,cursor:"pointer",fontSize:14}}>Annuler</button>
              <button onClick={()=>{onDeleteGroup(confirmDeleteGroup);setConfirmDeleteGroup(null);}} style={{padding:"12px",background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:12,color:"#EF4444",fontWeight:800,cursor:"pointer",fontSize:14}}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {showNewGroupModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.card,borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:360,border:`1px solid ${C.border}`}}>
            <h3 style={{color:C.text,fontSize:20,fontWeight:900,margin:"0 0 6px"}}>Nouveau programme</h3>
            <p style={{color:C.muted,fontSize:13,margin:"0 0 20px"}}>Donne un nom à ton programme</p>
            <input autoFocus value={newGroupName} onChange={e=>setNewGroupName(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&newGroupName.trim()){onAddCustomGroup(newGroupName.trim());setShowNewGroupModal(false);}}}
              placeholder="ex: PPL Perso, Été 2025…"
              style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:16,fontWeight:600,padding:"13px 14px",outline:"none",boxSizing:"border-box",marginBottom:20}}/>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowNewGroupModal(false)} style={{flex:1,padding:"12px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.muted,fontWeight:700,fontSize:14,cursor:"pointer"}}>Annuler</button>
              <button disabled={!newGroupName.trim()} onClick={()=>{if(newGroupName.trim()){const gName=newGroupName.trim();onAddCustomGroup(gName);setShowNewGroupModal(false);onCreateProgram(gName);}}}
                style={{flex:1,padding:"12px",background:newGroupName.trim()?`linear-gradient(135deg,${C.accent},${C.accent2})`:C.faint,border:"none",borderRadius:12,color:newGroupName.trim()?"white":C.muted,fontWeight:800,fontSize:14,cursor:newGroupName.trim()?"pointer":"default"}}>
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CREATION / EDITION PROGRAMME ────────────────────────────────────────────
function CreateProgramPage({onSave,onBack,existing,targetGroupName,C}) {
  const BtnC=mkBtn(C);
  const isEdit=!!existing;
  const initName=existing?.groupName||targetGroupName||"";

  const isAddSeance=!!targetGroupName&&!isEdit;
  const [step,setStep]=useState(isEdit?"config":isAddSeance?"seanceName":"name");
  const [progName,setProgName]=useState(initName);
  const [editingProgName,setEditingProgName]=useState(false);
  const [progNameDraft,setProgNameDraft]=useState(initName);
  const [seanceNameDraft,setSeanceNameDraft]=useState("");

  const [workouts,setWorkouts]=useState(()=>{
    if(isEdit) return [{wid:existing.id||Date.now(),name:existing.name||"Séance 1",exercises:existing.exercises||[]}];
    return [{wid:Date.now(),name:"Séance 1",exercises:[]}];
  });
  const [activeWi,setActiveWi]=useState(0);
  const [editingWi,setEditingWi]=useState(null);
  const [wNameDraft,setWNameDraft]=useState("");
  const [dragWiFrom,setDragWiFrom]=useState(null);
  const [dragWiOver,setDragWiOver]=useState(null);
  const dragWiRefs=useRef([]);
  const dragWiMetaRef=useRef({fromIdx:0,startY:0,itemHeight:64,lastY:0});
  const startDragW=(wi,clientY)=>{
    const el=dragWiRefs.current[wi];
    const h=el?el.getBoundingClientRect().height+10:64;
    dragWiMetaRef.current={fromIdx:wi,startY:clientY,itemHeight:h,lastY:clientY};
    setDragWiFrom(wi);setDragWiOver(wi);
    const getNewIdx=(y,len)=>Math.max(0,Math.min(len-1,Math.round(dragWiMetaRef.current.fromIdx+(y-dragWiMetaRef.current.startY)/dragWiMetaRef.current.itemHeight)));
    const onMove=ev=>{
      ev.preventDefault();
      const y=ev.touches?ev.touches[0].clientY:ev.clientY;
      dragWiMetaRef.current.lastY=y;
      setWorkouts(cur=>{setDragWiOver(getNewIdx(y,cur.length));return cur;});
    };
    const onEnd=()=>{
      document.removeEventListener("touchmove",onMove);
      document.removeEventListener("touchend",onEnd);
      document.removeEventListener("mousemove",onMove);
      document.removeEventListener("mouseup",onEnd);
      setWorkouts(cur=>{
        const toIdx=getNewIdx(dragWiMetaRef.current.lastY,cur.length);
        const fi=dragWiMetaRef.current.fromIdx;
        setDragWiFrom(null);setDragWiOver(null);
        if(fi===toIdx)return cur;
        const arr=[...cur];const[item]=arr.splice(fi,1);arr.splice(toIdx,0,item);
        setActiveWi(toIdx);
        return arr;
      });
    };
    document.addEventListener("touchmove",onMove,{passive:false});
    document.addEventListener("touchend",onEnd);
    document.addEventListener("mousemove",onMove);
    document.addEventListener("mouseup",onEnd);
  };

  const [filter,setFilter]=useState("Tous");
  const [search,setSearch]=useState("");
  const [dragExFrom,setDragExFrom]=useState(null);
  const [dragExOver,setDragExOver]=useState(null);
  const dragExItemRefs=useRef([]);
  const dragExMetaRef=useRef({fromIdx:0,startY:0,itemHeight:56,lastY:0});
  const startDragEx=(ei,clientY)=>{
    const el=dragExItemRefs.current[ei];
    const h=el?el.getBoundingClientRect().height+11:56;
    dragExMetaRef.current={fromIdx:ei,startY:clientY,itemHeight:h,lastY:clientY};
    setDragExFrom(ei);setDragExOver(ei);
    const getNewIdx=(y,len)=>Math.max(0,Math.min(len-1,Math.round(dragExMetaRef.current.fromIdx+(y-dragExMetaRef.current.startY)/dragExMetaRef.current.itemHeight)));
    const onMove=ev=>{
      ev.preventDefault();
      const y=ev.touches?ev.touches[0].clientY:ev.clientY;
      dragExMetaRef.current.lastY=y;
      setSel(cur=>{setDragExOver(getNewIdx(y,cur.length));return cur;});
    };
    const onEnd=()=>{
      document.removeEventListener("touchmove",onMove);
      document.removeEventListener("touchend",onEnd);
      document.removeEventListener("mousemove",onMove);
      document.removeEventListener("mouseup",onEnd);
      setSel(cur=>{
        const toIdx=getNewIdx(dragExMetaRef.current.lastY,cur.length);
        const fi=dragExMetaRef.current.fromIdx;
        setDragExFrom(null);setDragExOver(null);
        if(fi===toIdx)return cur;
        const arr=[...cur];const[item]=arr.splice(fi,1);arr.splice(toIdx,0,item);return arr;
      });
    };
    document.addEventListener("touchmove",onMove,{passive:false});
    document.addEventListener("touchend",onEnd);
    document.addEventListener("mousemove",onMove);
    document.addEventListener("mouseup",onEnd);
  };
  const [showCustom,setShowCustom]=useState(false);

  const activeW=workouts[activeWi]||{exercises:[]};
  const sel=activeW.exercises||[];
  const setSel=updater=>setWorkouts(ws=>ws.map((w,i)=>i!==activeWi?w:{...w,exercises:typeof updater==="function"?updater(w.exercises):updater}));

  const toggleEx=ex=>setSel(p=>p.find(e=>e.id===ex.id)?p.filter(e=>e.id!==ex.id):[...p,{...ex,restTime:90,plannedSets:[{reps:10,weight:0},{reps:10,weight:0},{reps:10,weight:0}]}]);
  const updPS=(id,si,f,v)=>setSel(p=>p.map(e=>e.id!==id?e:{...e,plannedSets:e.plannedSets.map((s,ii)=>ii===si?{...s,[f]:v}:s)}));
  const addSet=id=>setSel(p=>p.map(e=>e.id!==id?e:{...e,plannedSets:[...e.plannedSets,{reps:e.plannedSets[e.plannedSets.length-1]?.reps||10,weight:e.plannedSets[e.plannedSets.length-1]?.weight||0}]}));
  const remSet=id=>setSel(p=>p.map(e=>e.id!==id||e.plannedSets.length<=1?e:{...e,plannedSets:e.plannedSets.slice(0,-1)}));

  const filtered=EX.filter(ex=>(filter==="Tous"||ex.muscle===filter)&&ex.name.toLowerCase().includes(search.toLowerCase()));

  const addWorkout=()=>{
    const newW={wid:Date.now()+Math.random(),name:`Séance ${workouts.length+1}`,exercises:[]};
    const newIdx=workouts.length;
    setWorkouts(p=>[...p,newW]);
    setActiveWi(newIdx);
    setFilter("Tous");setSearch("");
    setStep("exercises");
  };
  const deleteWorkout=wi=>{setWorkouts(p=>p.filter((_,i)=>i!==wi));setActiveWi(0);};
  const renameWorkout=(wi,nm)=>setWorkouts(p=>p.map((w,i)=>i!==wi?w:{...w,name:nm||w.name}));


  const handleSave=()=>{
    const gName=progName.trim()||targetGroupName||"Mon Programme";
    if(isEdit){
      const w=workouts[0];
      if(!w||!w.exercises?.length)return;
      onSave({id:existing.id||Date.now(),name:w.name,exercises:w.exercises,groupName:existing.groupName||targetGroupName||null,isCustom:true});
    } else if(isAddSeance){
      const w=workouts[0];
      if(!w||!w.exercises?.length)return;
      onSave([{id:w.wid||Date.now(),name:w.name,exercises:w.exercises,groupName:targetGroupName,isCustom:true}]);
    } else {
      const progsToSave=workouts.filter(w=>w.exercises.length>0).map((w,i)=>({
        id:w.wid||Date.now()+i,name:w.name,exercises:w.exercises,groupName:gName,isCustom:true,
      }));
      if(!progsToSave.length)return;
      onSave(progsToSave);
    }
  };

  // ── ÉTAPE 0 : Nom de la séance (ajout séance à programme existant) ──────────
  if(step==="seanceName")return(
    <div style={{position:"fixed",inset:0,background:C.bg,overflowY:"auto",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
      <div style={{padding:"0 0 32px",display:"flex",alignItems:"center",gap:14}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:0}}>←</button>
        <div>
          <p style={{color:C.accent,fontSize:11,letterSpacing:2,fontWeight:700,margin:"0 0 2px"}}>{targetGroupName}</p>
          <h2 style={{color:C.text,fontSize:20,fontWeight:900,margin:0}}>NOUVELLE SÉANCE</h2>
        </div>
      </div>
      <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:10}}>NOM DE LA SÉANCE</label>
      <input autoFocus value={seanceNameDraft} onChange={e=>setSeanceNameDraft(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter"&&seanceNameDraft.trim()){
          setWorkouts([{wid:Date.now(),name:seanceNameDraft.trim(),exercises:[]}]);
          setActiveWi(0);setFilter("Tous");setSearch("");setStep("exercises");
        }}}
        placeholder="ex: Push, Jambes, Full Body…"
        style={{width:"100%",background:C.surface,border:`1.5px solid ${seanceNameDraft.trim()?C.accent:C.border}`,borderRadius:14,color:C.text,fontSize:18,fontWeight:600,padding:"15px 16px",outline:"none",boxSizing:"border-box",marginBottom:32}}/>
      <BtnC full onClick={()=>{
        if(!seanceNameDraft.trim())return;
        setWorkouts([{wid:Date.now(),name:seanceNameDraft.trim(),exercises:[]}]);
        setActiveWi(0);setFilter("Tous");setSearch("");setStep("exercises");
      }} disabled={!seanceNameDraft.trim()}>CHOISIR LES EXERCICES →</BtnC>
    </div>
  );

  // ── ÉTAPE 1 : Nom du programme ─────────────────────────────────────────────
  if(step==="name")return(
    <div style={{position:"fixed",inset:0,background:C.bg,overflowY:"auto",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
      <div style={{padding:"0 0 32px",display:"flex",alignItems:"center",gap:14}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:0}}>←</button>
        <h2 style={{color:C.text,fontSize:20,fontWeight:900,margin:0}}>NOUVEAU PROGRAMME</h2>
      </div>
      <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:10}}>NOM DU PROGRAMME</label>
      <input autoFocus value={progName} onChange={e=>setProgName(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter"&&progName.trim()){setWorkouts([{wid:Date.now(),name:"Séance 1",exercises:[]}]);setActiveWi(0);setStep("programme");}}}
        placeholder="ex: Push Pull Legs, Full Body…"
        style={{width:"100%",background:C.surface,border:`1.5px solid ${progName.trim()?C.accent:C.border}`,borderRadius:14,color:C.text,fontSize:18,fontWeight:600,padding:"15px 16px",outline:"none",boxSizing:"border-box",marginBottom:32}}/>
      <BtnC full onClick={()=>{if(!progName.trim())return;setWorkouts([{wid:Date.now(),name:"Séance 1",exercises:[]}]);setActiveWi(0);setStep("programme");}} disabled={!progName.trim()}>SUIVANT →</BtnC>
    </div>
  );

  // ── ÉTAPE 2 : Séances du programme ────────────────────────────────────────
  if(step==="programme")return(
    <div style={{position:"fixed",inset:0,background:C.bg,overflowY:"auto",padding:"calc(env(safe-area-inset-top) + 20px) 20px 120px",boxSizing:"border-box"}}>
      <div style={{padding:"0 0 20px",display:"flex",alignItems:"center",gap:12}}>
        <button onClick={()=>(isEdit||isAddSeance)?onBack():setStep("name")} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:0,flexShrink:0}}>←</button>
        <div style={{flex:1,minWidth:0}}>
          {editingProgName
            ? <input autoFocus value={progNameDraft} onChange={e=>setProgNameDraft(e.target.value)}
                onBlur={()=>{if(progNameDraft.trim())setProgName(progNameDraft.trim());setEditingProgName(false);}}
                onKeyDown={e=>{if(e.key==="Enter"){if(progNameDraft.trim())setProgName(progNameDraft.trim());setEditingProgName(false);}}}
                style={{width:"100%",background:"transparent",border:"none",borderBottom:`2px solid ${C.accent}`,color:C.text,fontSize:18,fontWeight:900,outline:"none",padding:"2px 0"}}/>
            : <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>{setProgNameDraft(progName);setEditingProgName(true);}}>
                <h2 style={{color:C.text,fontSize:18,fontWeight:900,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{progName}</h2>
                <span style={{color:C.muted,fontSize:13,flexShrink:0}}>✎</span>
              </div>
          }
          <p style={{color:C.muted,fontSize:12,margin:"2px 0 0"}}>{workouts.length} séance{workouts.length!==1?"s":""}</p>
        </div>
        {workouts.some(w=>w.exercises.length>0)&&(
          <button onClick={handleSave} style={{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:10,padding:"8px 14px",color:"white",fontWeight:800,fontSize:13,cursor:"pointer",flexShrink:0}}>
            Enregistrer
          </button>
        )}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
        {workouts.map((w,wi)=>{
          const exCount=w.exercises.length;
          const totalSets=w.exercises.reduce((a,e)=>a+(e.plannedSets?.length||0),0);
          return(
            <div key={w.wid} ref={el=>dragWiRefs.current[wi]=el}
              style={{background:dragWiOver===wi&&dragWiFrom!==null&&dragWiFrom!==wi?`${C.accent}08`:C.card,border:`1px solid ${dragWiOver===wi&&dragWiFrom!==null&&dragWiFrom!==wi?C.accent:C.border}`,borderRadius:16,overflow:"hidden",transition:"all 0.15s",opacity:dragWiFrom===wi?0.45:1}}>
              <div style={{padding:"12px 14px",display:"flex",alignItems:"center",gap:10}}>
                <div
                  onMouseDown={ev=>{ev.preventDefault();startDragW(wi,ev.clientY);}}
                  onTouchStart={ev=>{ev.preventDefault();startDragW(wi,ev.touches[0].clientY);}}
                  style={{flexShrink:0,cursor:"grab",padding:"4px 8px",display:"flex",alignItems:"center",justifyContent:"center",touchAction:"none",userSelect:"none"}}>
                  <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
                    {[1,6,11].map(y=>(
                      <line key={y} x1="2" y1={y+1} x2="14" y2={y+1} stroke={C.muted} strokeWidth="1.8" strokeLinecap="round" opacity="0.6"/>
                    ))}
                  </svg>
                </div>
                <div style={{width:32,height:32,borderRadius:9,background:`${C.accent}20`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{color:C.accent,fontWeight:900,fontSize:13}}>{wi+1}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  {editingWi===wi
                    ? <input autoFocus value={wNameDraft} onChange={e=>setWNameDraft(e.target.value)}
                        onBlur={()=>{renameWorkout(wi,wNameDraft);setEditingWi(null);}}
                        onKeyDown={e=>{if(e.key==="Enter"){renameWorkout(wi,wNameDraft);setEditingWi(null);}}}
                        style={{width:"100%",background:"transparent",border:"none",borderBottom:`1.5px solid ${C.accent}`,color:C.text,fontWeight:800,fontSize:14,outline:"none",padding:"2px 0"}}/>
                    : <div style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}} onClick={()=>{setWNameDraft(w.name);setEditingWi(wi);}}>
                        <p style={{color:C.text,fontWeight:800,fontSize:14,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.name}</p>
                        <span style={{color:C.muted,fontSize:11,flexShrink:0}}>✎</span>
                      </div>
                  }
                  <p style={{color:C.muted,fontSize:11,margin:"1px 0 0"}}>{exCount} exo{exCount!==1?"s":""}{totalSets>0?` · ${totalSets} séries`:""}</p>
                </div>
                {workouts.length>1&&<button onClick={()=>deleteWorkout(wi)} style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"6px 10px",color:C.red,fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>×</button>}
              </div>
              <div style={{borderTop:`1px solid ${C.border}`,display:"flex"}}>
                <button onClick={()=>{setActiveWi(wi);setFilter("Tous");setSearch("");setStep("exercises");}}
                  style={{flex:1,padding:"10px",background:"none",border:"none",borderRight:exCount>0?`1px solid ${C.border}`:"none",color:C.accent,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  {exCount===0?"+ Exercices":"Exercices"}
                </button>
                {exCount>0&&<button onClick={()=>{setActiveWi(wi);setStep("config");}}
                  style={{flex:1,padding:"10px",background:"none",border:"none",color:C.muted,fontWeight:700,fontSize:12,cursor:"pointer"}}>Configurer</button>}
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={addWorkout} style={{width:"100%",padding:"14px",background:"transparent",border:`1.5px dashed ${C.accent}`,borderRadius:14,color:C.accent,fontWeight:800,fontSize:14,cursor:"pointer",marginBottom:16}}>
        + Ajouter une séance
      </button>
      {workouts.some(w=>w.exercises.length>0)&&<BtnC full onClick={handleSave}>{isEdit?"METTRE À JOUR":"ENREGISTRER LE PROGRAMME"}</BtnC>}
    </div>
  );

  // ── ÉTAPE 3 : Sélection exercices ─────────────────────────────────────────
  if(step==="exercises")return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,display:"flex",flexDirection:"column"}}>
      {showCustom&&<AddCustomExModal onAdd={ex=>{setSel(p=>[...p,{...ex,restTime:90,plannedSets:[{reps:10,weight:0},{reps:10,weight:0},{reps:10,weight:0}]}]);setShowCustom(false);}} onClose={()=>setShowCustom(false)} isTabata={false} C={C}/>}
      <div style={{padding:"calc(env(safe-area-inset-top) + 20px) 20px 0",background:C.bg,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <button onClick={()=>setStep(isAddSeance?"seanceName":"programme")} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:0}}>←</button>
          <div style={{flex:1,minWidth:0}}>
            <h2 style={{color:C.text,fontSize:15,fontWeight:900,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{activeW.name}</h2>
            <p style={{color:C.muted,fontSize:12,margin:0}}>{sel.length} sélectionné{sel.length>1?"s":""}</p>
          </div>
          {sel.length>0&&<button onClick={()=>setStep("config")} style={{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:10,padding:"8px 14px",color:"white",fontWeight:800,fontSize:13,cursor:"pointer",flexShrink:0}}>Configurer →</button>}
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un exercice…"
          style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:16,padding:"10px 13px",outline:"none",boxSizing:"border-box",marginBottom:10}}/>
        <div style={{display:"flex",gap:8,paddingBottom:10,overflowX:"auto",scrollbarWidth:"none"}}>
          {["Tous",...ALL_MUSCLES].map(m=><button key={m} onClick={()=>setFilter(m)} style={{flexShrink:0,padding:"6px 13px",borderRadius:20,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,background:filter===m?C.accent:C.faint,color:filter===m?"white":C.muted}}>{m}</button>)}
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto",minHeight:0,WebkitOverflowScrolling:"touch",padding:"8px 20px 100px",display:"flex",flexDirection:"column",gap:7}}>
        <div onClick={()=>setShowCustom(true)} style={{background:`${C.accent}10`,border:`1px dashed ${C.accent}`,borderRadius:12,padding:"11px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:`${C.accent}20`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><span style={{color:C.accent,fontSize:18,fontWeight:900}}>+</span></div>
          <p style={{color:C.accent,fontWeight:800,margin:0,fontSize:13}}>Exercice personnalisé</p>
        </div>
        {filtered.map(ex=>{
          const isSel=!!sel.find(e=>e.id===ex.id);const meta=MM[ex.muscle]||{};
          return(
            <div key={ex.id} onClick={()=>toggleEx(ex)} style={{background:isSel?`${C.accent}0F`:C.card,border:`1px solid ${isSel?C.accent:C.border}`,borderRadius:12,padding:"10px 13px",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1,minWidth:0}}>
                <p style={{color:C.text,fontWeight:700,margin:"0 0 3px",fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ex.name}</p>
                <Tag label={ex.muscle} color={meta.color||C.muted} bg={meta.bg||C.faint}/>
              </div>
              <div style={{width:26,height:26,borderRadius:"50%",background:isSel?C.accent:C.faint,color:isSel?"white":C.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,flexShrink:0}}>{isSel?"✓":"+"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── ÉTAPE 4 : Configuration séries/reps/repos ─────────────────────────────
  return(
    <div style={{position:"fixed",inset:0,background:C.bg,overflowY:"auto",padding:"calc(env(safe-area-inset-top) + 20px) 20px 120px",boxSizing:"border-box"}}>
      <div style={{padding:"0 0 12px",display:"flex",alignItems:"center",gap:12}}>
        <button onClick={()=>isEdit?onBack():setStep("exercises")} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:0}}>←</button>
        <div style={{flex:1,minWidth:0}}>
          <h2 style={{color:C.text,fontSize:15,fontWeight:900,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{activeW.name}</h2>
          <p style={{color:C.muted,fontSize:11,margin:0}}>≡ pour réordonner</p>
        </div>
        <button onClick={()=>setStep("exercises")} style={{background:C.faint,border:"none",borderRadius:9,padding:"6px 11px",color:C.muted,fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>+ Exercices</button>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:11,marginBottom:20}}>
        {sel.map((ex,ei)=>(
          <div key={ex.id} ref={el=>dragExItemRefs.current[ei]=el}
            style={{background:dragExOver===ei&&dragExFrom!==null&&dragExFrom!==ei?`${C.accent}18`:C.card,border:`1px solid ${dragExOver===ei&&dragExFrom!==null&&dragExFrom!==ei?C.accent:C.border}`,borderRadius:16,overflow:"hidden",transition:"background 0.1s,border 0.1s",opacity:dragExFrom===ei?0.45:1}}>
            <div style={{padding:"10px 13px",display:"flex",alignItems:"center",gap:9,borderBottom:`1px solid ${C.border}`}}>
              <div
                onMouseDown={ev=>{ev.preventDefault();startDragEx(ei,ev.clientY);}}
                onTouchStart={ev=>{ev.preventDefault();startDragEx(ei,ev.touches[0].clientY);}}
                style={{flexShrink:0,cursor:"grab",padding:"4px 8px",display:"flex",alignItems:"center",justifyContent:"center",touchAction:"none",userSelect:"none"}}>
                <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
                  {[1,6,11].map(y=>(
                    <line key={y} x1="2" y1={y+1} x2="14" y2={y+1} stroke={C.muted} strokeWidth="1.8" strokeLinecap="round" opacity="0.6"/>
                  ))}
                </svg>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{color:C.text,fontWeight:700,margin:0,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ex.name}</p>
                <Tag label={ex.muscle} color={MM[ex.muscle]?.color||C.muted} bg={MM[ex.muscle]?.bg||C.faint}/>
              </div>
              <button onClick={()=>toggleEx(ex)} style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"6px 10px",color:C.red,fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>×</button>
            </div>
            <div style={{padding:"9px 13px"}}>
              <div style={{display:"grid",gridTemplateColumns:"22px 1fr 1fr",gap:5,marginBottom:5}}>
                <span/><span style={{color:C.muted,fontSize:10,letterSpacing:2,textAlign:"center"}}>REPS</span><span style={{color:C.muted,fontSize:10,letterSpacing:2,textAlign:"center"}}>POIDS kg</span>
              </div>
              {ex.plannedSets.map((ps,si)=>(
                <div key={si} style={{display:"grid",gridTemplateColumns:"22px 1fr 1fr",gap:5,marginBottom:5,alignItems:"center"}}>
                  <span style={{background:C.faint,color:C.muted,borderRadius:"50%",width:20,height:20,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,margin:"0 auto"}}>{si+1}</span>
                  {[{f:"reps",v:ps.reps,step:1,min:1,dec:0},{f:"weight",v:ps.weight,step:2.5,min:0,dec:1}].map(({f,v,step,min,dec})=>(
                    <div key={f} style={{display:"flex",alignItems:"center",gap:3}}>
                      <button onClick={()=>updPS(ex.id,si,f,parseFloat(Math.max(min,v-step).toFixed(dec)))} style={{background:C.faint,border:"none",borderRadius:6,width:26,height:26,color:C.text,fontSize:15,cursor:"pointer",flexShrink:0}}>−</button>
                      <span style={{color:f==="weight"?C.accent:C.text,fontWeight:800,flex:1,textAlign:"center",fontSize:14}}>{dec===0?v:v.toFixed(dec)}</span>
                      <button onClick={()=>updPS(ex.id,si,f,parseFloat((v+step).toFixed(dec)))} style={{background:C.faint,border:"none",borderRadius:6,width:26,height:26,color:C.accent,fontSize:15,cursor:"pointer",flexShrink:0}}>+</button>
                    </div>
                  ))}
                </div>
              ))}
              <div style={{display:"flex",gap:7,marginTop:8}}>
                <button onClick={()=>remSet(ex.id)} disabled={ex.plannedSets.length<=1} style={{flex:1,padding:"6px",background:C.faint,border:"none",borderRadius:8,color:C.muted,fontSize:12,fontWeight:700,cursor:"pointer",opacity:ex.plannedSets.length<=1?0.3:1}}>− Série</button>
                <button onClick={()=>addSet(ex.id)} style={{flex:1,padding:"6px",background:`${C.accent}15`,border:`1px solid ${C.accent}30`,borderRadius:8,color:C.accent,fontSize:12,fontWeight:700,cursor:"pointer"}}>+ Série</button>
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,padding:"9px 0",borderTop:`1px solid ${C.faint}`}}>
                <span style={{color:C.muted,fontSize:11,letterSpacing:1}}>REPOS</span>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <button onClick={()=>setSel(p=>p.map(e=>e.id!==ex.id?e:{...e,restTime:Math.max(15,(e.restTime||90)-15)}))} style={{background:C.faint,border:"none",borderRadius:6,width:28,height:28,color:C.text,fontSize:16,cursor:"pointer"}}>−</button>
                  <span style={{color:C.accent,fontWeight:800,fontSize:13,minWidth:44,textAlign:"center"}}>{fmtRest(ex.restTime||90)}</span>
                  <button onClick={()=>setSel(p=>p.map(e=>e.id!==ex.id?e:{...e,restTime:Math.min(300,(e.restTime||90)+15)}))} style={{background:C.faint,border:"none",borderRadius:6,width:28,height:28,color:C.accent,fontSize:16,cursor:"pointer"}}>+</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!isEdit&&<BtnC full onClick={()=>setStep(isAddSeance?"seanceName":"programme")}>← Retour aux {isAddSeance?"exercices":"séances"}</BtnC>}
      {(isEdit||isAddSeance)&&sel.length>0&&<div style={{marginTop:isEdit?0:10}}><BtnC full onClick={handleSave}>{isEdit?"METTRE À JOUR":"ENREGISTRER LA SÉANCE"}</BtnC></div>}
    </div>
  );
}


export { HomePage, CreateProgramPage, GenerateProgramPage, ExPicker, AddCustomExModal, ALL_MUSCLES, fmtRest };
